import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import { listingsAPI, imeiAPI, uploadAPI, pricingAPI, sellerVerificationAPI, authAPI } from '../api/client'
import { normalizeImeiDigits, sanitizeImeiFieldInput } from '../utils/extractImei'
import ImeiScannerModal from './ImeiScannerModal'
import { type InBoxCompleteness } from '../utils/inBoxCompleteness'
import { isLikelyImageFile } from '../utils/imageFile'
import { modelsRoughlyMatch, resolvePhoneModelIdFromHints } from '../utils/phoneModelMatch'
import YearWheelPicker from './YearWheelPicker'
import ImeiFindGuide from './ImeiFindGuide'
import PhotoTutorialModal from './PhotoTutorialModal'
import ListingModelPicker from './ListingModelPicker'
import ConditionPickerGrid from './ConditionPickerGrid'
import InBoxPickerGrid from './InBoxPickerGrid'
import UsageDurationPickerGrid from './UsageDurationPickerGrid'
import WarrantyFields from './WarrantyFields'
import ListingSpecPanel from './ListingSpecPanel'
import ListingPhotoCropModal from './ListingPhotoCropModal'
import SellFormPanel from './SellFormPanel'
import CollapseSlot from './CollapseSlot'
import PartsAuthenticityFields from './PartsAuthenticityFields'
import BatteryTransparencyBuilderPanel from './BatteryTransparencyBuilderPanel'
import { specBatteryColor } from './ListingSpecIcons'
import SellerPayoutPreview from './SellerPayoutPreview'
import DefectMarksMultiPhoto from './DefectMarksMultiPhoto'
import type { DefectMark } from './DefectMarkPad'
import {
  defectMarksForSubmit,
  formatDefectDescription,
  newPhotoId,
  removeMarksForPhotoId,
  renumberDefectDisplayIndices,
} from '../utils/defectMarks'
import { inferReleaseYear } from '../utils/inferReleaseYear'
import { fetchPhoneModelsCached, invalidatePhoneModelsCache } from '../utils/phoneModelsCache'
import {
  isBasicPhoneModel,
  isBasicPhoneCatalogModel,
  isBasicPhoneSellManufacturer,
  batteryOriginalFromParts,
  compactPartsAuthenticity,
  mergeBatteryIntoParts,
  EMPTY_PARTS_AUTHENTICITY,
  parseStorageLabelToGb,
  parseStorageGbFromTitle,
  PAYMENTS_ENABLED,
  type PartsAuthenticityMap,
  LISTING_RELEASE_YEAR_FLOOR,
  resolvePhoneModelMinReleaseYear,
  defaultListingReleaseYearForModel,
  clampListingReleaseYear,
  resolvePhoneModelStorageLabels,
  defaultListingStorageForModel,
  isStorageAllowedForModel,
  formatListingShareDevice,
  conditionForListingSubmit,
  conditionForPricing,
} from '@redevicer/shared'
import { STORAGE_OPTIONS } from '../utils/storageOptions'
import {
  clearSellListingDraft,
  draftToPhotos,
  loadSellListingDraft,
  photosToDraft,
  saveSellListingDraft,
  type SellListingDraftV1,
} from '../utils/sellListingDraft'
import { takeQuickSellPrefill, type QuickSellPrefillV1 } from '../utils/quickSellPrefill'
import { useMatchMedia } from '../hooks/useMatchMedia'
import {
  BuilderStepGate,
  ListingBuilderContext,
} from './listing-builder/ListingBuilderContext'
import ListingBuilderStepper from './listing-builder/ListingBuilderStepper'
import ListingPreview from './listing-builder/ListingPreview'
import { mapDraftToListingPreview } from '../utils/mapDraftToListingPreview'
import type { ListingBuilderDraft } from '../utils/listingBuilderDraft'
import BuilderPhotosStep, { type BuilderPhotoSlot } from './listing-builder/BuilderPhotosStep'
import BuilderPriceStep from './listing-builder/BuilderPriceStep'
import BuilderContactStep from './listing-builder/BuilderContactStep'
import BuilderStepHead from './listing-builder/BuilderStepHead'
import {
  BUILDER_PHONE_DIAL_OPTIONS,
  composeSellerPhone,
  defaultBuilderPhoneDial,
} from '../utils/builderPhoneDial'

const BUILDER_PHOTO_SLOT_ORDER: BuilderPhotoSlot[] = [
  'front',
  'back',
  'side',
  'top',
  'bottom',
  'camera',
  'other',
]

type PendingPhoto = { id: string; file: File; url: string }

export type SellFormMode = 'with-imei' | 'smartphone-no-imei' | 'basic-phone' | 'super-fast'

interface ListingFormProps {
  token?: string | null
  userPreview?: { email?: string; full_name?: string } | null
  mode: SellFormMode
  onBack?: () => void
  onCreated?: (listingId: number) => void
  onGuestAuthed?: (token: string, user: unknown) => void
  onFlashMessage?: (message: string) => void
  onOpenAccountSettings?: () => void
}

interface ListingImage {
  id: number
  thumbnail_url: string
  display_url: string
  is_primary: boolean
}

interface PhoneModelOpt {
  id: number
  manufacturer: string
  model_name: string
  category?: string | null
  release_year?: number | null
  specs_json?: string | null
}

interface Suggestion {
  quick_sale_eur: number
  balanced_eur: number
  patient_eur: number
  recommended_eur: number
  methodology: string
  photo_adjustment_applied?: boolean
  photo_cosmetic_hint?: string | null
  photo_summary_key?: string | null
  photo_tips_key?: string | null
  photo_recognized?: boolean | null
  photo_quality?: string | null
  photo_suggested_condition?: string | null
  model_match?: string | null
  model_mismatch?: boolean | null
  detected_model_label?: string | null
  expected_model_label?: string | null
  pricing_unreliable?: boolean
  expected_color?: string | null
  detected_color?: string | null
  color_mismatch?: boolean | null
  assess_engine?: string | null
  summary_key?: string
  tips_key?: string | null
  photo_detail?: string | null
  photo_price_multiplier?: number | null
}

interface ListingHints {
  phone_model_id?: number | null
  suggested_title?: string | null
  release_year?: number | null
  confidence?: string | null
  brand?: string | null
  model_name?: string | null
  tac?: string | null
  device_color?: string | null
}

const CURRENT_YEAR = new Date().getFullYear()

type GuestSignInReason = 'email' | 'phone'

function mapGuestLoginError(detail: string | undefined, fallback: string, t: (key: string) => string): string {
  if (!detail) return fallback
  if (/invalid credentials/i.test(detail)) {
    return t('listing.guestSignInInvalidCredentials')
  }
  return detail
}

function guestAccountConflict(err: unknown): GuestSignInReason | null {
  const ax = err as { response?: { status?: number; data?: { detail?: unknown } } }
  if (ax.response?.status !== 409) return null
  const detail = ax.response?.data?.detail
  if (typeof detail !== 'string') return null
  const lower = detail.toLowerCase()
  if (lower.includes('email')) return 'email'
  if (lower.includes('phone')) return 'phone'
  return null
}

function buildAutoTitle(
  model: PhoneModelOpt | null,
  storageGb: string,
  year: string,
): string {
  if (!model) return storageGb || ''
  const parts = [formatListingShareDevice(model.manufacturer, model.model_name)]
  if (storageGb) parts.push(storageGb)
  const yr = parseInt(year, 10)
  if (Number.isFinite(yr) && yr > 0) parts.push(String(yr))
  return parts.join(' ')
}

export default function ListingForm({
  token = null,
  userPreview = null,
  mode,
  onBack,
  onCreated,
  onGuestAuthed,
  onFlashMessage,
  onOpenAccountSettings,
}: ListingFormProps) {
  const { t, i18n } = useTranslation()
  const isBasicMode = mode === 'basic-phone'
  const isSuperFast = mode === 'super-fast'
  const isDesktopWide = useMatchMedia('(min-width: 1280px)')
  /** Stepped sell flow (stepper, illustrated pickers) — web mobile + desktop. */
  const steppedBuilder = !isSuperFast
  /** Wide two-column layout with live preview + fixed viewport chrome. */
  const builderWideLayout = isDesktopWide && steppedBuilder
  const [builderStep, setBuilderStep] = useState(1)
  const [builderStepDirection, setBuilderStepDirection] = useState<
    'forward' | 'back' | null
  >(null)
  const [builderPreviewPhotoIndex, setBuilderPreviewPhotoIndex] = useState(0)
  const showImeiSection = mode === 'with-imei'
  const skipImeiByPath =
    mode === 'smartphone-no-imei' || mode === 'basic-phone' || mode === 'super-fast'
  const [title, setTitle] = useState('')
  const [price, setPrice] = useState('')
  const [condition, setCondition] = useState(isSuperFast ? 'good' : '')
  const [storage, setStorage] = useState('')
  const [batteryHealth, setBatteryHealth] = useState('100')
  const [batteryCycles, setBatteryCycles] = useState('')
  const [batteryOriginal, setBatteryOriginal] = useState(true)
  const [batteryTestedAt, setBatteryTestedAt] = useState('')
  const [releaseYear, setReleaseYear] = useState('')
  const [releaseYearUnknown, setReleaseYearUnknown] = useState(false)
  const [imei, setImei] = useState('')
  const [imeiVerified, setImeiVerified] = useState(false)
  const [imeiSkipped, setImeiSkipped] = useState(skipImeiByPath)
  const [imeiError, setImeiError] = useState('')
  const [loading, setLoading] = useState(false)
  const [uploadingImage, setUploadingImage] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  const [photoPickError, setPhotoPickError] = useState('')
  const conditionTouchedRef = useRef(false)
  const titleTouchedRef = useRef(false)
  const priceTouchedRef = useRef(false)
  const lastImeiHintsRef = useRef<ListingHints | null>(null)
  const preserveImeiSpecOnNextModelRef = useRef(false)
  const pendingImeiStorageGbRef = useRef<number | null>(null)
  const [imeiDeviceColor, setImeiDeviceColor] = useState<string | null>(null)

  const [phoneModels, setPhoneModels] = useState<PhoneModelOpt[]>([])
  const [phoneModelId, setPhoneModelId] = useState('')
  const [modelsLoading, setModelsLoading] = useState(false)
  const [modelsLoadFailed, setModelsLoadFailed] = useState(false)
  const [suggestion, setSuggestion] = useState<Suggestion | null>(null)
  const [pricingLoading, setPricingLoading] = useState(false)
  const [pricingError, setPricingError] = useState('')
  const [imeiAutofillNote, setImeiAutofillNote] = useState('')
  const [imeiAutofillTone, setImeiAutofillTone] = useState<'muted' | 'accent'>('muted')
  const [imeiLookupDeviceLine, setImeiLookupDeviceLine] = useState('')
  const galleryInputRef = useRef<HTMLInputElement>(null)
  const cameraCaptureRef = useRef<HTMLInputElement>(null)
  const imeiLabelInputRef = useRef<HTMLInputElement>(null)
  const [imeiOcrLoading, setImeiOcrLoading] = useState(false)
  const pendingPhotosRef = useRef<PendingPhoto[]>([])
  const [pendingPhotos, setPendingPhotos] = useState<PendingPhoto[]>([])
  const [dragPhotoIdx, setDragPhotoIdx] = useState<number | null>(null)
  const [dragOverPhotoIdx, setDragOverPhotoIdx] = useState<number | null>(null)
  const [imeiScannerOpen, setImeiScannerOpen] = useState(false)
  const [shipmentMethod, setShipmentMethod] = useState<'courier' | 'locker' | 'handover'>('handover')
  const [sellerCity, setSellerCity] = useState('')
  const [catalogCityOptions, setCatalogCityOptions] = useState<string[]>([])
  const [userCities, setUserCities] = useState<string[]>([])
  const shippingIncluded = false
  const [usageDuration, setUsageDuration] = useState('')
  const [hasFactoryWarranty, setHasFactoryWarranty] = useState(false)
  const [warrantyRemaining, setWarrantyRemaining] = useState('')
  const [defectMarks, setDefectMarks] = useState<DefectMark[]>([])
  const [defectPhotoId, setDefectPhotoId] = useState<string | null>(null)
  const [selectedDefectId, setSelectedDefectId] = useState<string | null>(null)
  const [photoSlots, setPhotoSlots] = useState<Record<string, BuilderPhotoSlot | undefined>>({})
  const [inBoxCompleteness, setInBoxCompleteness] = useState<InBoxCompleteness | null>(
    isSuperFast ? 'phone_only' : null,
  )
  const [partsAuth, setPartsAuth] = useState<PartsAuthenticityMap>({ ...EMPTY_PARTS_AUTHENTICITY })
  const [imeiBlockedStolen, setImeiBlockedStolen] = useState(false)
  const [imeiEuNoBalance, setImeiEuNoBalance] = useState(false)
  const [photoTutorialOpen, setPhotoTutorialOpen] = useState(false)
  const [showMoreSettings, setShowMoreSettings] = useState(false)
  const [cropQueue, setCropQueue] = useState<File[]>([])
  const [cropSource, setCropSource] = useState<File | string | null>(null)
  const [cropFileName, setCropFileName] = useState('photo.jpg')
  const [cropReplaceIndex, setCropReplaceIndex] = useState<number | null>(null)
  const [headerModelQuery, setHeaderModelQuery] = useState('')
  const [canSell, setCanSell] = useState(false)
  const [sellerFullName, setSellerFullName] = useState(userPreview?.full_name ?? '')
  const [sellerEmail, setSellerEmail] = useState(userPreview?.email ?? '')
  const [sellerPhone, setSellerPhone] = useState('')
  const [builderPhoneDial, setBuilderPhoneDial] = useState(() => defaultBuilderPhoneDial(i18n.language))
  const [guestSignInReason, setGuestSignInReason] = useState<GuestSignInReason | null>(null)
  const [guestSignInPassword, setGuestSignInPassword] = useState('')
  const [guestSignInError, setGuestSignInError] = useState('')
  const [guestForgotMode, setGuestForgotMode] = useState(false)
  const [guestForgotSent, setGuestForgotSent] = useState(false)
  const [guestForgotError, setGuestForgotError] = useState('')
  const [submitError, setSubmitError] = useState('')
  const draftRestoredRef = useRef(false)
  const guestSignInRef = useRef<HTMLDivElement>(null)
  const shouldScrollToGuestSignInRef = useRef(false)
  const pendingPublishRef = useRef<{
    listingData: Record<string, unknown>
    queuedPhotosSnapshot: PendingPhoto[]
  } | null>(null)

  const needsContact = !token || !canSell

  pendingPhotosRef.current = pendingPhotos

  useEffect(() => {
    if (pendingPhotos.length === 0) {
      setDefectPhotoId(null)
      return
    }
    if (!defectPhotoId || !pendingPhotos.some((p) => p.id === defectPhotoId)) {
      setDefectPhotoId(pendingPhotos[0].id)
    }
  }, [pendingPhotos, defectPhotoId])

  useEffect(() => {
    setPhotoSlots((prev) => {
      let changed = false
      const next = { ...prev }
      pendingPhotos.forEach((p, idx) => {
        if (!next[p.id]) {
          next[p.id] =
            BUILDER_PHOTO_SLOT_ORDER[Math.min(idx, BUILDER_PHOTO_SLOT_ORDER.length - 1)]
          changed = true
        }
      })
      for (const id of Object.keys(next)) {
        if (!pendingPhotos.some((p) => p.id === id)) {
          delete next[id]
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [pendingPhotos])

  async function buildSellDraft(guestReason: GuestSignInReason | null): Promise<SellListingDraftV1> {
    const photoSource =
      pendingPublishRef.current?.queuedPhotosSnapshot?.length
        ? pendingPublishRef.current.queuedPhotosSnapshot
        : pendingPhotos
    return {
      v: 1,
      mode,
      savedAt: Date.now(),
      guestSignInReason: guestReason,
      sellerFullName,
      sellerEmail,
      sellerPhone,
      title,
      price,
      condition,
      storage,
      batteryHealth,
      batteryCycles,
      batteryOriginal,
      batteryTestedAt,
      releaseYear,
      releaseYearUnknown,
      imei,
      imeiVerified,
      imeiSkipped,
      phoneModelId,
      shipmentMethod,
      usageDuration,
      hasFactoryWarranty,
      warrantyRemaining,
      defectMarks,
      photoSlots: Object.fromEntries(
        Object.entries(photoSlots).filter((entry): entry is [string, BuilderPhotoSlot] => Boolean(entry[1])),
      ),
      inBoxCompleteness,
      partsAuth,
      listingData: pendingPublishRef.current?.listingData ?? null,
      photos: await photosToDraft(photoSource),
    }
  }

  async function persistSellDraft(guestReason: GuestSignInReason | null = guestSignInReason) {
    if (!guestReason) return
    try {
      saveSellListingDraft(await buildSellDraft(guestReason))
    } catch {
      /* ignore quota errors */
    }
  }

  async function applyQuickSellPrefill(prefill: QuickSellPrefillV1) {
    setPhoneModelId(prefill.phoneModelId)
    setStorage(prefill.storage)
    setBatteryHealth(prefill.batteryHealth)
    setReleaseYear(prefill.releaseYear)
    setReleaseYearUnknown(prefill.releaseYearUnknown)
    pendingPhotosRef.current.forEach((p) => URL.revokeObjectURL(p.url))
    const restoredPhotos = prefill.photos.length > 0 ? await draftToPhotos(prefill.photos) : []
    pendingPhotosRef.current = restoredPhotos
    setPendingPhotos(restoredPhotos)
  }

  async function applySellDraft(draft: SellListingDraftV1) {
    setSellerFullName(draft.sellerFullName)
    setSellerEmail(draft.sellerEmail)
    setSellerPhone(draft.sellerPhone)
    setTitle(draft.title)
    setPrice(draft.price)
    setCondition(draft.condition)
    setStorage(draft.storage)
    setBatteryHealth(draft.batteryHealth)
    setBatteryCycles(draft.batteryCycles)
    setBatteryOriginal(draft.batteryOriginal)
    setBatteryTestedAt(draft.batteryTestedAt)
    setReleaseYear(draft.releaseYear)
    setReleaseYearUnknown(draft.releaseYearUnknown)
    setImei(draft.imei)
    setImeiVerified(draft.imeiVerified)
    setImeiSkipped(draft.imeiSkipped)
    setPhoneModelId(draft.phoneModelId)
    setShipmentMethod(draft.shipmentMethod)
    setUsageDuration(draft.usageDuration)
    setHasFactoryWarranty(draft.hasFactoryWarranty)
    setWarrantyRemaining(draft.warrantyRemaining)
    setDefectMarks(draft.defectMarks)
    setPhotoSlots((draft.photoSlots ?? {}) as Record<string, BuilderPhotoSlot | undefined>)
    setInBoxCompleteness(draft.inBoxCompleteness)
    setPartsAuth(draft.partsAuth)
    if (draft.guestSignInReason) {
      setGuestSignInReason(draft.guestSignInReason)
      setGuestForgotMode(false)
      setGuestForgotSent(false)
      setGuestForgotError('')
    }
    pendingPhotosRef.current.forEach((p) => URL.revokeObjectURL(p.url))
    const restoredPhotos = draft.photos.length > 0 ? await draftToPhotos(draft.photos) : []
    pendingPhotosRef.current = restoredPhotos
    setPendingPhotos(restoredPhotos)
    if (draft.listingData) {
      pendingPublishRef.current = {
        listingData: draft.listingData,
        queuedPhotosSnapshot: restoredPhotos,
      }
    }
  }

  useEffect(() => {
    if (draftRestoredRef.current) return
    draftRestoredRef.current = true
    const draft = loadSellListingDraft()
    if (draft && draft.mode === mode) {
      void applySellDraft(draft)
      return
    }
    if (mode === 'smartphone-no-imei' || mode === 'super-fast') {
      const prefill = takeQuickSellPrefill()
      if (prefill) void applyQuickSellPrefill(prefill)
    }
  }, [mode])

  useEffect(() => {
    if (!guestSignInReason) return
    const timer = window.setTimeout(() => {
      void persistSellDraft(guestSignInReason)
    }, 400)
    return () => window.clearTimeout(timer)
  }, [guestSignInReason])

  useEffect(() => {
    if (!guestSignInReason || !shouldScrollToGuestSignInRef.current) return
    shouldScrollToGuestSignInRef.current = false
    const timer = window.setTimeout(() => {
      guestSignInRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 80)
    return () => window.clearTimeout(timer)
  }, [guestSignInReason])

  useEffect(() => {
    if (!isSuperFast) return
    window.scrollTo(0, 0)
  }, [isSuperFast])

  useEffect(() => {
    sellerVerificationAPI
      .getCities()
      .then((res) => setCatalogCityOptions((res.data?.cities as string[]) ?? []))
      .catch(() => setCatalogCityOptions([]))
  }, [])

  useEffect(() => {
    if (!token) {
      setCanSell(false)
      return
    }
    sellerVerificationAPI
      .getStatus(token)
      .then((res) => {
        setCanSell(Boolean(res.data?.can_sell))
        const cities = Array.isArray(res.data?.seller_cities)
          ? (res.data.seller_cities as string[])
          : []
        setUserCities(cities)
        setSellerCity((prev) => prev || cities[0] || '')
      })
      .catch(() => setCanSell(false))
  }, [token])

  useEffect(() => {
    if (!isSuperFast || !phoneModelId) return
    setSuggestion(null)
  }, [phoneModelId, isSuperFast])

  useEffect(() => {
    const apply = (q: string) => {
      if (!q.trim()) return
      setHeaderModelQuery(q.trim())
    }
    try {
      const stored = sessionStorage.getItem('redevicer:sell-model-query')
      if (stored) {
        apply(stored)
        sessionStorage.removeItem('redevicer:sell-model-query')
      }
    } catch {
      /* ignore */
    }
    const onEvt = (e: Event) => {
      const q = String((e as CustomEvent<{ q?: string }>).detail?.q ?? '')
      apply(q)
    }
    window.addEventListener('redevicer:sell-model-query', onEvt)
    return () => window.removeEventListener('redevicer:sell-model-query', onEvt)
  }, [])

  useEffect(() => {
    return () => {
      pendingPhotosRef.current.forEach((p) => URL.revokeObjectURL(p.url))
    }
  }, [])

  useEffect(() => {
    // Load models once the user is past the IMEI step — verified OR explicitly skipped.
    if (!imeiVerified && !imeiSkipped) {
      setPhoneModels([])
      setPhoneModelId('')
      setReleaseYear('')
      setSuggestion(null)
      setModelsLoading(false)
      return
    }

    setModelsLoading(true)
    setModelsLoadFailed(false)
    fetchPhoneModelsCached()
      .then((data) => {
        setPhoneModels(data)
        setModelsLoadFailed(data.length === 0)
        const hints = lastImeiHintsRef.current
        if (hints && data.length) {
          const id = resolvePhoneModelIdFromHints(data, hints)
          setPhoneModelId(id)
        } else if (data.length) {
          setPhoneModelId((prev) => {
            const exists = prev && data.some((m: PhoneModelOpt) => String(m.id) === prev)
            return exists ? prev : ''
          })
        }
      })
      .catch(() => {
        setPhoneModels([])
        setModelsLoadFailed(true)
      })
      .finally(() => setModelsLoading(false))
  }, [imeiVerified, imeiSkipped])

  function retryPhoneModelsLoad() {
    if (!imeiVerified && !imeiSkipped) return
    invalidatePhoneModelsCache()
    setModelsLoading(true)
    setModelsLoadFailed(false)
    fetchPhoneModelsCached()
      .then((data) => {
        setPhoneModels(data)
        setModelsLoadFailed(data.length === 0)
      })
      .catch(() => {
        setPhoneModels([])
        setModelsLoadFailed(true)
      })
      .finally(() => setModelsLoading(false))
  }

  function storageLabelForGb(gb: number, options: readonly string[]): string | null {
    for (const opt of options) {
      if (parseStorageLabelToGb(opt) === gb) return opt
    }
    return null
  }

  function applyStorageFromHints(
    hints: ListingHints | null | undefined,
    options: readonly string[],
  ) {
    if (!hints) return
    const gb =
      parseStorageGbFromTitle(hints.model_name ?? '') ??
      parseStorageGbFromTitle(hints.suggested_title ?? '')
    if (gb == null) return
    const label = storageLabelForGb(gb, options)
    if (label) {
      setStorage(label)
      pendingImeiStorageGbRef.current = null
    } else {
      pendingImeiStorageGbRef.current = gb
    }
  }

  function applyReleaseYearFromHints(hints: ListingHints | null | undefined) {
    if (!hints) return
    let yr: number | null = null
    if (hints.release_year != null) {
      yr = hints.release_year
    } else {
      yr = inferReleaseYear(hints.brand, hints.model_name)
    }
    if (yr == null) return
    const minY = resolvePhoneModelMinReleaseYear(hints.brand, hints.model_name, hints.release_year)
    setReleaseYear(String(clampListingReleaseYear(yr, minY)))
    setReleaseYearUnknown(false)
  }

  function syncPhoneModelFromHints(hints: ListingHints | null | undefined) {
    if (!hints) return
    lastImeiHintsRef.current = hints
    applyReleaseYearFromHints(hints)
    setImeiDeviceColor(hints.device_color?.trim() || null)
    fetchPhoneModelsCached()
      .then((data) => {
        setPhoneModels(data)
        const id = resolvePhoneModelIdFromHints(data, hints)
        setPhoneModelId(id)
        const apiModel = hints.model_name?.trim()
        if (apiModel && !id) {
          const device = [hints.brand, apiModel].filter(Boolean).join(' ')
          setImeiLookupDeviceLine(t('listing.imeiLookupPickModel', { device }))
          setImeiAutofillTone('muted')
        } else if (apiModel && id) {
          const row = data.find((m: PhoneModelOpt) => String(m.id) === id)
          if (row && !modelsRoughlyMatch(apiModel, row.model_name)) {
            setImeiLookupDeviceLine(
              t('listing.imeiModelMismatch', { api: apiModel, catalog: row.model_name }),
            )
            setPhoneModelId('')
          } else {
            setImeiLookupDeviceLine('')
          }
        }
      })
      .catch(() => {})
  }

  const batteryGuideDevice = useMemo(() => {
    const selected = phoneModels.find((m) => String(m.id) === phoneModelId)
    if (selected) {
      return { manufacturer: selected.manufacturer, modelName: selected.model_name }
    }
    const hints = lastImeiHintsRef.current
    return {
      manufacturer: hints?.brand ?? null,
      modelName: hints?.model_name ?? null,
    }
  }, [phoneModels, phoneModelId, imeiVerified, imeiAutofillNote])

  const filteredPhoneModels = useMemo(() => {
    if (isBasicMode) {
      return phoneModels.filter(
        (m) => isBasicPhoneCatalogModel(m) && isBasicPhoneSellManufacturer(m.manufacturer),
      )
    }
    return phoneModels.filter((m) => !isBasicPhoneCatalogModel(m))
  }, [phoneModels, isBasicMode])

  const selectedModel = useMemo(
    () => phoneModels.find((m) => String(m.id) === phoneModelId) ?? null,
    [phoneModels, phoneModelId],
  )

  const isBasicPhone =
    isBasicMode || (selectedModel != null && isBasicPhoneModel(selectedModel))

  const modelMinReleaseYear = useMemo(() => {
    if (!selectedModel || isBasicPhone) return LISTING_RELEASE_YEAR_FLOOR
    return resolvePhoneModelMinReleaseYear(
      selectedModel.manufacturer,
      selectedModel.model_name,
      selectedModel.release_year,
    )
  }, [selectedModel, isBasicPhone])

  const modelStorageOptions = useMemo(() => {
    if (!selectedModel || isBasicPhone) return STORAGE_OPTIONS
    return resolvePhoneModelStorageLabels(
      selectedModel.manufacturer,
      selectedModel.model_name,
      STORAGE_OPTIONS,
      selectedModel.specs_json,
    )
  }, [selectedModel, isBasicPhone])

  const prevSelectedModelIdRef = useRef<string>('')

  useEffect(() => {
    if (!selectedModel || isBasicPhone) return
    const id = String(selectedModel.id)
    if (prevSelectedModelIdRef.current === id) return
    prevSelectedModelIdRef.current = id

    if (preserveImeiSpecOnNextModelRef.current) {
      preserveImeiSpecOnNextModelRef.current = false
      const pendingGb = pendingImeiStorageGbRef.current
      if (pendingGb != null) {
        const label = storageLabelForGb(pendingGb, modelStorageOptions)
        if (label) {
          setStorage(label)
          pendingImeiStorageGbRef.current = null
        }
      }
      return
    }

    const yr = defaultListingReleaseYearForModel(selectedModel)
    setReleaseYearUnknown(false)
    setReleaseYear(String(yr))
    const defaultStorage = defaultListingStorageForModel(
      selectedModel.manufacturer,
      selectedModel.model_name,
      selectedModel.specs_json,
    )
    if (defaultStorage) setStorage(defaultStorage)
  }, [selectedModel, isBasicPhone, modelStorageOptions])

  useEffect(() => {
    const pendingGb = pendingImeiStorageGbRef.current
    if (pendingGb == null || !modelStorageOptions.length) return
    const label = storageLabelForGb(pendingGb, modelStorageOptions)
    if (label) {
      setStorage(label)
      pendingImeiStorageGbRef.current = null
    }
  }, [modelStorageOptions])

  useEffect(() => {
    if (!selectedModel || isBasicPhone || !storage) return
    if (
      isStorageAllowedForModel(
        selectedModel.manufacturer,
        selectedModel.model_name,
        storage,
        parseStorageLabelToGb,
        selectedModel.specs_json,
      )
    ) {
      return
    }
    const fallback = defaultListingStorageForModel(
      selectedModel.manufacturer,
      selectedModel.model_name,
      selectedModel.specs_json,
    )
    setStorage(fallback ?? modelStorageOptions[0] ?? '')
  }, [selectedModel, isBasicPhone, storage, modelStorageOptions])

  useEffect(() => {
    if (releaseYearUnknown || !selectedModel || isBasicPhone) return
    const yr = parseInt(releaseYear, 10)
    if (!Number.isFinite(yr) || yr >= modelMinReleaseYear) return
    setReleaseYear(String(modelMinReleaseYear))
  }, [modelMinReleaseYear, releaseYear, releaseYearUnknown, selectedModel, isBasicPhone])

  useEffect(() => {
    if (isBasicMode || !phoneModelId) return
    const exists = filteredPhoneModels.some((m) => String(m.id) === phoneModelId)
    if (!exists) setPhoneModelId('')
  }, [filteredPhoneModels, phoneModelId, isBasicMode])

  useEffect(() => {
    priceTouchedRef.current = false
    setPrice('')
    setSuggestion(null)
  }, [phoneModelId])

  function handleBasicModelResolved(model: PhoneModelOpt) {
    const yr = defaultListingReleaseYearForModel(model)
    setReleaseYearUnknown(false)
    setReleaseYear(String(yr))
    titleTouchedRef.current = false
  }

  async function handleCustomPhoneLabel(label: string) {
    const trimmed = label.trim()
    if (trimmed.length < 2) return
    try {
      const res = isBasicMode
        ? await listingsAPI.resolveBasicPhoneModel(trimmed)
        : await listingsAPI.resolveCustomPhoneModel(trimmed, true)
      const m = res.data as PhoneModelOpt
      setPhoneModels((prev) => (prev.some((x) => x.id === m.id) ? prev : [...prev, m]))
      setPhoneModelId(String(m.id))
      titleTouchedRef.current = false
      setTitle(`${m.manufacturer} ${m.model_name}`.trim())
      handleBasicModelResolved(m)
    } catch {
      alert(t('listing.basicPhoneCustomFailed'))
    }
  }

  const coverPhotoKey = useMemo(() => {
    const file = pendingPhotos[0]?.file
    if (!file) return ''
    return `${file.name}:${file.size}:${file.lastModified}`
  }, [pendingPhotos])

  useEffect(() => {
    // AI price suggestion — smartphones only; basic phones: seller sets price manually.
    if (isBasicPhone) {
      setSuggestion(null)
      setPricingLoading(false)
      setPricingError('')
      return
    }

    if (!imeiVerified && !imeiSkipped) {
      setSuggestion(null)
      setPricingLoading(false)
      setPricingError('')
      return
    }

    const id = parseInt(phoneModelId, 10)
    if (!Number.isFinite(id) || id <= 0) {
      setSuggestion(null)
      setPricingError('')
      return
    }
    const bh = isBasicPhone ? NaN : parseInt(batteryHealth, 10)
    const yr = parseInt(releaseYear, 10)
    const storageGb = parseStorageLabelToGb(storage)
    const basePayload = {
      phone_model_id: id,
      condition: conditionForPricing(condition),
      battery_health: Number.isFinite(bh) ? bh : undefined,
      release_year: isBasicPhone ? undefined : Number.isFinite(yr) ? yr : undefined,
      storage_gb: isBasicPhone ? undefined : storageGb != null ? storageGb : undefined,
      expected_device_color: isBasicPhone ? undefined : imeiDeviceColor,
      in_box_completeness: inBoxCompleteness ?? undefined,
    }

    const coverFile = !isBasicPhone ? pendingPhotos[0]?.file : undefined
    let cancelled = false

    const timer = window.setTimeout(() => {
      if (cancelled) return
      setPricingLoading(true)
      setPricingError('')

      const req =
        coverFile && token
          ? pricingAPI.suggestWithPhotoPreview(token, basePayload, coverFile)
          : pricingAPI.suggest(basePayload)

      req
        .then((res) => {
          if (cancelled) return
          setSuggestion(res.data)
          const rec = res.data?.recommended_eur
          if (typeof rec === 'number' && Number.isFinite(rec) && rec > 0 && !priceTouchedRef.current) {
            setPrice(rec.toFixed(2))
          }
        })
        .catch(() => {
          if (cancelled) return
          setSuggestion(null)
          setPricingError(t('listing.pricingManualFallback'))
        })
        .finally(() => {
          if (!cancelled) setPricingLoading(false)
        })
    }, 350)

    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [
    imeiVerified,
    imeiSkipped,
    phoneModelId,
    condition,
    batteryHealth,
    releaseYear,
    storage,
    token,
    imeiDeviceColor,
    inBoxCompleteness,
    isBasicPhone,
    coverPhotoKey,
    t,
  ])

  const priceNum = parseFloat(price.replace(',', '.'))
  const isGreedy =
    suggestion &&
    Number.isFinite(priceNum) &&
    priceNum > suggestion.recommended_eur * 1.06

  function resolveContactFullName(): string {
    const manual = sellerFullName.trim()
    if (manual) return manual
    const fromProfile = userPreview?.full_name?.trim()
    if (fromProfile) return fromProfile
    const email = sellerEmail.trim()
    if (email.includes('@')) {
      const local = email
        .split('@')[0]
        ?.replace(/[._+-]+/g, ' ')
        .trim()
      if (local) return local.slice(0, 120)
    }
    return ''
  }

  const submitBlockers = useMemo(() => {
    const blockers: string[] = []
    if (!imeiVerified && !imeiSkipped) blockers.push(t('listing.verifyFirst'))
    if (imeiBlockedStolen) blockers.push(t('listing.imeiBlockedStolen'))
    if (modelsLoading) blockers.push(t('listing.modelsLoading'))
    const pmid = parseInt(phoneModelId, 10)
    if (!Number.isFinite(pmid) || pmid <= 0) blockers.push(t('listing.selectModelFirst'))
    if (!isBasicPhone && !isSuperFast && !storage) {
      blockers.push(t('listing.storageRequired', 'Pasirinkite atmintį (GB).'))
    }
    if (!isSuperFast && !inBoxCompleteness) {
      blockers.push(t('listing.kitRequired', 'Pasirinkite komplektaciją.'))
    }
    const parsedPrice = parseFloat(price.replace(',', '.'))
    if (!price.trim() || !Number.isFinite(parsedPrice) || parsedPrice <= 0) {
      blockers.push(t('listing.priceRequired', 'Įveskite pageidaujamą kainą.'))
    }
    if (needsContact) {
      const email = sellerEmail.trim()
      const phone = sellerPhone.trim()
      if (!token) {
        if (!email && !phone) {
          blockers.push(t('listing.builder.contactMethodHint'))
        }
      } else if (!canSell && !resolveContactFullName()) {
        blockers.push(t('listing.builder.contactNeedName'))
      }
    }
    return blockers
  }, [
    imeiVerified,
    imeiSkipped,
    imeiBlockedStolen,
    modelsLoading,
    phoneModelId,
    isBasicPhone,
    isSuperFast,
    storage,
    inBoxCompleteness,
    price,
    needsContact,
    sellerFullName,
    sellerEmail,
    sellerPhone,
    token,
    canSell,
    userPreview?.full_name,
    t,
  ])

  const canSubmit = submitBlockers.length === 0 && !loading

  const builderStepComplete = useMemo(() => {
    const pmid = parseInt(phoneModelId, 10)
    const hasModel = Number.isFinite(pmid) && pmid > 0
    const imeiOk = imeiVerified || imeiSkipped
    const storageOk = isBasicPhone || Boolean(storage)
    const parsedPrice = parseFloat(price.replace(',', '.'))
    const priceOk = Boolean(price.trim()) && Number.isFinite(parsedPrice) && parsedPrice > 0
    return {
      1: imeiOk && hasModel && storageOk,
      2: true,
      3: true,
      4: Boolean(inBoxCompleteness),
      5: true,
      6: priceOk,
      7: true,
    } as Record<number, boolean>
  }, [
    phoneModelId,
    imeiVerified,
    imeiSkipped,
    isBasicPhone,
    storage,
    inBoxCompleteness,
    price,
  ])

  const builderMaxReachable = useMemo(() => {
    let max = 1
    for (let s = 1; s <= 7; s += 1) {
      if (builderStepComplete[s]) max = Math.min(7, s + 1)
      else break
    }
    return max
  }, [builderStepComplete])

  const builderCanContinue = builderStepComplete[builderStep] ?? false

  const photosConditionHint = useMemo(() => {
    if (defectMarks.length > 0) return { count: 0, message: '' }
    if (condition === 'fair') {
      return { count: 1, message: t('listing.builder.conditionUndocumentedFair') }
    }
    if (condition === 'damaged') {
      return { count: 1, message: t('listing.builder.conditionUndocumentedDamaged') }
    }
    return { count: 0, message: '' }
  }, [condition, defectMarks.length, t])

  useEffect(() => {
    if (!steppedBuilder) setBuilderStep(1)
  }, [steppedBuilder])

  useEffect(() => {
    if (!builderWideLayout) return
    document.documentElement.classList.add('sell-builder-desktop')
    return () => document.documentElement.classList.remove('sell-builder-desktop')
  }, [builderWideLayout])

  useEffect(() => {
    if (!steppedBuilder || builderWideLayout) return
    document.documentElement.classList.add('sell-builder-mobile')
    return () => document.documentElement.classList.remove('sell-builder-mobile')
  }, [steppedBuilder, builderWideLayout])

  useEffect(() => {
    if (!steppedBuilder) return
    document.documentElement.classList.toggle('sell-builder-step-condition', builderStep === 2)
    document.documentElement.classList.toggle('sell-builder-step-history', builderStep === 3)
    document.documentElement.classList.toggle('sell-builder-step-photos', builderStep === 5)
    document.documentElement.classList.toggle('sell-builder-step-contact', builderStep === 7)
    return () => {
      document.documentElement.classList.remove('sell-builder-step-condition')
      document.documentElement.classList.remove('sell-builder-step-history')
      document.documentElement.classList.remove('sell-builder-step-photos')
      document.documentElement.classList.remove('sell-builder-step-contact')
    }
  }, [steppedBuilder, builderStep])

  useEffect(() => {
    if (builderStep > builderMaxReachable) setBuilderStep(builderMaxReachable)
  }, [builderStep, builderMaxReachable])

  function goBuilderNext() {
    if (!builderCanContinue || builderStep >= 7) return
    setBuilderStepDirection('forward')
    setBuilderStep((s) => Math.min(7, s + 1))
  }

  function goBuilderBack() {
    if (builderStep <= 1) return
    setBuilderStepDirection('back')
    setBuilderStep((s) => Math.max(1, s - 1))
  }

  function goBuilderToStep(target: number) {
    if (target === builderStep) return
    setBuilderStepDirection(target > builderStep ? 'forward' : 'back')
    setBuilderStep(target)
  }

  async function handleBuilderSaveDraft() {
    try {
      await saveSellListingDraft(await buildSellDraft(guestSignInReason))
      onFlashMessage?.(t('listing.builder.saveDraft'))
    } catch {
      /* ignore */
    }
  }

  useEffect(() => {
    if (titleTouchedRef.current) return
    if (!selectedModel && !storage) return
    if (isBasicPhone) {
      setTitle(`${selectedModel!.manufacturer} ${selectedModel!.model_name}`.trim())
      return
    }
    setTitle(buildAutoTitle(selectedModel, storage, releaseYear))
  }, [selectedModel, storage, releaseYear, isBasicPhone])

  function pickStorage(value: string) {
    setStorage(value)
    if (!titleTouchedRef.current) {
      setTitle(buildAutoTitle(selectedModel, value, releaseYear))
    }
  }

  const releaseYearWheelValue = releaseYearUnknown
    ? null
    : (() => {
        const yr = parseInt(releaseYear, 10)
        if (Number.isFinite(yr)) return yr
        return selectedModel?.release_year ?? null
      })()

  function handleReleaseYearPick(y: number) {
    setReleaseYearUnknown(false)
    setReleaseYear(String(y))
  }

  function handleReleaseYearUnknown() {
    setReleaseYearUnknown(true)
    setReleaseYear('')
  }

  const releaseYearPickerInline = (
    <YearWheelPicker
      value={releaseYearWheelValue}
      onChange={handleReleaseYearPick}
      min={modelMinReleaseYear}
      unknown={releaseYearUnknown}
      onUnknown={handleReleaseYearUnknown}
      unknownLabel={t('listing.releaseYearUnknown', 'Nežinoma')}
      showUnknownButton={false}
      compact={steppedBuilder}
    />
  )

  const releaseYearUnknownButton = (
    <button
      type="button"
      className={`year-wheel-picker__unknown year-wheel-picker__unknown--cell${releaseYearUnknown ? ' year-wheel-picker__unknown--active' : ''}`}
      aria-pressed={releaseYearUnknown}
      onClick={handleReleaseYearUnknown}
    >
      {t('listing.releaseYearUnknown', 'Nežinoma')}
    </button>
  )

  const batteryLevel = parseInt(batteryHealth, 10) || 100
  const batteryTone = specBatteryColor(batteryLevel)

  function openGalleryPicker(e?: React.MouseEvent) {
    e?.stopPropagation()
    if (pendingPhotos.length >= 10) return
    galleryInputRef.current?.click()
  }

  function applyImeiCheckPayload(data: {
    is_valid: boolean
    is_stolen?: boolean
    is_blacklisted?: boolean
    is_financed?: boolean
    imei_eu_balance_required?: boolean
    listing_hints?: unknown
    error?: string
    device_lookup?: 'stub' | 'api' | 'imei.eu' | 'imei.info' | 'osmocom' | 'dev-mock'
    dev_mock?: boolean
    message?: string
    imei_eu_charged_usd?: number | null
    imei_eu_services_used?: number | null
  }) {
    if (data.is_valid) {
      const blocked =
        !!data.is_stolen || !!data.is_blacklisted || !!data.is_financed
      setImeiVerified(true)
      setImeiBlockedStolen(blocked)
      setImeiEuNoBalance(!!data.imei_eu_balance_required)
      if (data.is_stolen || data.is_blacklisted) {
        setImeiError(t('listing.imeiStolenWarning'))
      } else if (data.is_financed) {
        setImeiError(t('listing.imeiFinancedWarning'))
      } else if (data.imei_eu_balance_required) {
        setImeiError('')
      } else {
        setImeiError('')
      }
      const hints = data.listing_hints as ListingHints | undefined
      if (hints?.suggested_title && !titleTouchedRef.current && !title.trim()) {
        setTitle(hints.suggested_title)
      }
      preserveImeiSpecOnNextModelRef.current = true
      applyReleaseYearFromHints(hints)
      applyStorageFromHints(hints, modelStorageOptions)
      syncPhoneModelFromHints(hints)
      let autofillNote = ''
      if (data.imei_eu_balance_required) {
        autofillNote = t('listing.imeiEuBalanceRequired')
        setImeiAutofillTone('muted')
        const brand = hints?.brand?.trim()
        const model = hints?.model_name?.trim()
        if (!hints?.phone_model_id && (brand || model)) {
          const device = [brand, model].filter(Boolean).join(' ')
          setImeiLookupDeviceLine(t('listing.imeiLookupPickModel', { device }))
        }
      } else if (data.device_lookup === 'dev-mock' || data.dev_mock) {
        if (hints?.confidence === 'api' && hints?.phone_model_id) {
          autofillNote =
            data.message ||
            t('listing.imeiAutofillDevMockMatched', {
              model: [hints.brand, hints.model_name].filter(Boolean).join(' '),
            })
          setImeiAutofillTone('accent')
        } else if (hints?.confidence === 'tac_stub' && hints?.phone_model_id) {
          autofillNote = t('listing.imeiAutofillDevMockTac', {
            model: [hints.brand, hints.model_name].filter(Boolean).join(' '),
          })
          setImeiAutofillTone('accent')
        } else {
          autofillNote = data.message || t('listing.imeiAutofillDevMockUnknownTac')
          setImeiAutofillTone('muted')
          if (!hints?.phone_model_id) {
            setPhoneModelId('')
            setTitle('')
            setReleaseYear('')
          }
        }
      } else if (hints?.confidence === 'api_not_found') {
        autofillNote = t('listing.imeiAutofillApiNotFound')
        setImeiAutofillTone('muted')
      } else if (hints?.confidence === 'tac_stub') {
        autofillNote = t('listing.imeiAutofillTac')
        setImeiAutofillTone('accent')
      } else if (hints?.confidence === 'guess') {
        autofillNote =
          data.device_lookup === 'stub'
            ? t('listing.imeiInfoStubNoTac', { tac: hints?.tac || '—' })
            : t('listing.imeiAutofillGuess')
        setImeiAutofillTone('muted')
      }
      if (autofillNote) setImeiAutofillNote(autofillNote)
      if (blocked) {
        setImeiLookupDeviceLine('')
      }
    } else {
      setImeiBlockedStolen(false)
      setImeiLookupDeviceLine('')
      setImeiError(data.error || t('listing.imeiInvalidFormat'))
    }
  }

  function applyImeiCheckSuccess(res: {
    data: {
      is_valid: boolean
      is_stolen?: boolean
      listing_hints?: unknown
      error?: string
      device_lookup?: 'stub' | 'api' | 'imei.eu' | 'dev-mock'
    dev_mock?: boolean
    }
  }) {
    applyImeiCheckPayload(res.data)
  }

  async function handleImeiLabelPhoto(fileList: FileList | null) {
    const file = fileList?.[0]
    if (!file || !token) return
    setImeiOcrLoading(true)
    setImeiError('')
    setImeiAutofillNote('')
    const fd = new FormData()
    fd.append('file', file)
    try {
      let payload: {
        imei?: string | null
        detail?: string | null
        check?: {
          is_valid: boolean
          is_stolen?: boolean
          listing_hints?: unknown
          error?: string
          device_lookup?: 'stub' | 'api' | 'imei.eu' | 'dev-mock'
    dev_mock?: boolean
        }
      }
      try {
        const res = await imeiAPI.scanLabelAndCheck(token, fd)
        payload = res.data as typeof payload
      } catch (scanErr: unknown) {
        const ax = scanErr as { response?: { status?: number } }
        if (ax.response?.status !== 404) throw scanErr
        const ocrRes = await imeiAPI.ocrExtractFromImage(token, fd)
        const digits = ocrRes.data?.imei
        if (!digits) {
          setImeiVerified(false)
          setImeiError(ocrRes.data?.detail || t('listing.imeiScan.noDigits'))
          return
        }
        setImei(digits)
        verifyIMEI(digits)
        return
      }
      if (payload.imei) {
        setImei(payload.imei)
      }
      if (payload.check) {
        applyImeiCheckPayload(payload.check)
      } else if (payload.imei) {
        verifyIMEI(payload.imei)
      } else {
        setImeiVerified(false)
        setImeiError(payload.detail || t('listing.imeiScan.noDigits'))
      }
    } catch (err: unknown) {
      const ax = err as { response?: { status?: number; data?: { detail?: string } } }
      if (ax.response?.status === 404) {
        setImeiError(t('listing.imeiApiNotFound'))
      } else {
        setImeiError(ax.response?.data?.detail || t('listing.imeiScan.ocrFailed'))
      }
    } finally {
      setImeiOcrLoading(false)
    }
  }

  function verifyIMEI(rawValue?: string) {
    const normalized = normalizeImeiDigits(rawValue ?? imei).slice(0, 15)
    if (normalized.length !== 15) {
      setImeiVerified(false)
      setImeiError(t('listing.imeiNeed15Digits'))
      return
    }
    setImeiError('')
    setImeiBlockedStolen(false)
    setImeiEuNoBalance(false)
    setImeiAutofillNote('')
    setImeiAutofillTone('muted')
    setImeiLookupDeviceLine('')
    setPhoneModelId('')
    setReleaseYear('')
    setTitle('')
    setImeiDeviceColor(null)
    lastImeiHintsRef.current = null
    setImei(normalized)
    const payload = normalized
    imeiAPI
      .check(payload)
      .then((res) => applyImeiCheckSuccess(res))
      .catch((err) => {
        const status = err.response?.status
        if (status === 404) {
          setImeiError(t('listing.imeiApiNotFound'))
          return
        }
        setImeiError(
          err.response?.data?.error ||
            err.response?.data?.detail ||
            t('listing.imeiCheckFailed'),
        )
      })
  }

  function queueGalleryFiles(fileList: FileList | null) {
    if (!fileList?.length) return
    const incoming: File[] = []
    let rejected = 0
    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i]
      if (!isLikelyImageFile(file)) {
        rejected++
        continue
      }
      incoming.push(file)
    }
    if (incoming.length === 0) {
      if (rejected > 0) setPhotoPickError(t('listing.photosPickUnsupported'))
      return
    }
    setPhotoPickError('')
    const room = 10 - pendingPhotos.length
    const allowed = incoming.slice(0, room)
    if (allowed.length === 0) return
    for (const file of allowed) {
      appendCroppedPhoto(file)
    }
  }

  function closePhotoCrop() {
    setCropSource(null)
    setCropQueue([])
    setCropReplaceIndex(null)
  }

  function appendCroppedPhoto(file: File) {
    const url = URL.createObjectURL(file)
    setPendingPhotos((prev) => {
      if (prev.length >= 10) {
        URL.revokeObjectURL(url)
        return prev
      }
      return [...prev, { id: newPhotoId(), file, url }]
    })
  }

  function replaceCroppedPhoto(index: number, file: File) {
    setPendingPhotos((prev) => {
      const old = prev[index]
      if (old) URL.revokeObjectURL(old.url)
      const url = URL.createObjectURL(file)
      const next = [...prev]
      next[index] = { id: old?.id ?? newPhotoId(), file, url }
      return next
    })
  }

  function onPhotoCropConfirm(file: File) {
    if (cropReplaceIndex != null) {
      replaceCroppedPhoto(cropReplaceIndex, file)
      closePhotoCrop()
      return
    }
    appendCroppedPhoto(file)
    if (cropQueue.length > 0) {
      const [next, ...rest] = cropQueue
      setCropQueue(rest)
      setCropFileName(next.name)
      setCropSource(next)
      return
    }
    closePhotoCrop()
  }

  function startEditPendingPhoto(index: number) {
    const item = pendingPhotos[index]
    if (!item) return
    setCropReplaceIndex(index)
    setCropQueue([])
    setCropFileName(item.file.name)
    setCropSource(item.file)
  }

  function addPendingGalleryFiles(fileList: FileList | null) {
    queueGalleryFiles(fileList)
  }

  function removePendingPhoto(index: number) {
    const removedId = pendingPhotos[index]?.id
    setPendingPhotos((prev) => {
      const p = prev[index]
      if (p) URL.revokeObjectURL(p.url)
      return prev.filter((_, i) => i !== index)
    })
    if (removedId) {
      setDefectMarks((prev) => {
        const next = renumberDefectDisplayIndices(removeMarksForPhotoId(prev, removedId))
        if (selectedDefectId && prev.some((m) => m.id === selectedDefectId && m.photo_id === removedId)) {
          setSelectedDefectId(null)
        }
        return next
      })
    }
  }

  function setPendingPrimary(index: number) {
    if (index <= 0) return
    reorderPendingPhotos(index, 0)
    setBuilderPreviewPhotoIndex(0)
  }

  function refreshPendingPhotoUrl(id: string) {
    setPendingPhotos((prev) =>
      prev.map((p) => {
        if (p.id !== id) return p
        URL.revokeObjectURL(p.url)
        return { ...p, url: URL.createObjectURL(p.file) }
      }),
    )
  }

  function reorderPendingPhotos(from: number, to: number) {
    if (from === to || from < 0 || to < 0) return
    setPendingPhotos((prev) => {
      if (from >= prev.length || to >= prev.length) return prev
      const next = [...prev]
      const [item] = next.splice(from, 1)
      next.splice(to, 0, item)
      return next
    })
    setCropReplaceIndex((prev) => {
      if (prev == null) return prev
      if (prev === from) return to
      if (from < prev && to >= prev) return prev - 1
      if (from > prev && to <= prev) return prev + 1
      return prev
    })
  }

  function onPhotoDragStart(index: number, e: React.DragEvent) {
    if ((e.target as HTMLElement).closest('button')) {
      e.preventDefault()
      return
    }
    setDragPhotoIdx(index)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', String(index))
  }

  function onPhotoDragOver(index: number, e: React.DragEvent) {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    if (dragOverPhotoIdx !== index) setDragOverPhotoIdx(index)
  }

  function onPhotoDrop(index: number, e: React.DragEvent) {
    e.preventDefault()
    const from =
      dragPhotoIdx ??
      (() => {
        const raw = e.dataTransfer.getData('text/plain')
        const parsed = Number.parseInt(raw, 10)
        return Number.isFinite(parsed) ? parsed : null
      })()
    if (from != null) reorderPendingPhotos(from, index)
    setDragPhotoIdx(null)
    setDragOverPhotoIdx(null)
  }

  function onPhotoDragEnd() {
    setDragPhotoIdx(null)
    setDragOverPhotoIdx(null)
  }

  function handleDrag(e: React.DragEvent) {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    if (e.dataTransfer.files?.length) {
      addPendingGalleryFiles(e.dataTransfer.files)
    }
  }

  async function publishListing(
    listingData: Record<string, unknown>,
    queuedPhotosSnapshot: PendingPhoto[],
    authToken: string | null,
  ) {
    const queuedFiles = queuedPhotosSnapshot.map((p) => p.file)
    const publishedPhone = composeSellerPhone(builderPhoneDial, sellerPhone)
    let activeToken = authToken ?? ''
    let newId: number

    if (!authToken) {
      const res = await listingsAPI.guestCreateListing({
        listing: listingData,
        seller: {
          full_name: resolveContactFullName() || sellerEmail.trim().split('@')[0] || 'Seller',
          ...(sellerEmail.trim() ? { email: sellerEmail.trim() } : {}),
          ...(publishedPhone ? { phone: publishedPhone } : {}),
        },
      })
      activeToken = String(res.data?.access_token ?? '')
      const listingPayload = res.data?.listing as { id?: number } | undefined
      const guestId = listingPayload?.id
      if (!activeToken || !Number.isFinite(guestId) || (guestId ?? 0) <= 0) {
        throw new Error(t('listing.createFailed'))
      }
      newId = guestId as number
      onGuestAuthed?.(activeToken, res.data.user)
    } else if (!canSell) {
      await sellerVerificationAPI.saveProfile(authToken, {
        seller_type: 'individual',
        full_name: resolveContactFullName(),
        contact_phone: publishedPhone || undefined,
      })
      const res = await listingsAPI.createListing(listingData, authToken)
      const createdId = res.data?.id as number | undefined
      if (!Number.isFinite(createdId) || (createdId ?? 0) <= 0) {
        throw new Error(t('listing.createFailed'))
      }
      setCanSell(true)
      newId = createdId as number
      activeToken = authToken
    } else {
      const res = await listingsAPI.createListing(listingData, authToken)
      const createdId = res.data?.id as number | undefined
      if (!Number.isFinite(createdId) || (createdId ?? 0) <= 0) {
        throw new Error(t('listing.createFailed'))
      }
      newId = createdId as number
      activeToken = authToken
    }

    const uploaded: ListingImage[] = []
    const uploadErrors: string[] = []
    if (queuedFiles.length > 0) {
      setUploadingImage(true)
      for (const file of queuedFiles) {
        try {
          const up = await uploadAPI.uploadListingImage(newId, file, activeToken)
          if (up.data?.id) uploaded.push(up.data as ListingImage)
        } catch (err: unknown) {
          const ax = err as { response?: { data?: { detail?: string } } }
          uploadErrors.push(ax.response?.data?.detail || t('listing.photoUploadFailed'))
        }
      }
      if (uploaded.length > 1) {
        try {
          await uploadAPI.reorderListingImages(
            newId,
            uploaded.map((img) => img.id),
            activeToken,
          )
          uploaded.forEach((img, idx) => {
            img.is_primary = idx === 0
          })
        } catch {
          /* keep upload order */
        }
      }
      setUploadingImage(false)
    }

    queuedPhotosSnapshot.forEach((p) => URL.revokeObjectURL(p.url))
    setPendingPhotos([])
    pendingPublishRef.current = null
    setGuestSignInReason(null)
    setGuestSignInPassword('')
    setGuestSignInError('')
    setGuestForgotMode(false)
    setGuestForgotSent(false)
    setGuestForgotError('')
    setSubmitError('')
    clearSellListingDraft()

    const createdMessage =
      uploadErrors.length > 0
        ? uploaded.length > 0
          ? `${t('listing.createdWithPhotos', { count: uploaded.length })}\n${uploadErrors[0]}`
          : `${t('listing.createdNoPhotos')}\n${uploadErrors[0]}`
        : uploaded.length > 0
          ? t('listing.createdWithPhotos', { count: uploaded.length })
          : t('listing.createdOk')
    onFlashMessage?.(createdMessage)
    setTitle('')
    setPrice('')
    setStorage('')
    setImei('')
    setImeiVerified(false)
    setReleaseYear('')
    setPhoneModelId('')
    setCondition('')
    conditionTouchedRef.current = false
    onCreated?.(newId)
  }

  async function handleGuestForgotPassword() {
    const email = sellerEmail.trim()
    if (!email) {
      setGuestForgotError(t('listing.guestContactRequired'))
      return
    }
    setLoading(true)
    setGuestForgotError('')
    try {
      await persistSellDraft(guestSignInReason)
      await authAPI.forgotPassword(email)
      setGuestForgotSent(true)
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } }; message?: string }
      setGuestForgotError(ax.response?.data?.detail || ax.message || t('auth.forgotPasswordFailed'))
    } finally {
      setLoading(false)
    }
  }

  async function handleGuestSignInAndContinue() {
    const email = sellerEmail.trim()
    const password = guestSignInPassword
    if (!email || !password) {
      setGuestSignInError(t('listing.guestSignInMissing'))
      return
    }
    const pending = pendingPublishRef.current
    if (!pending) {
      setGuestSignInError(t('listing.createFailed'))
      return
    }

    setLoading(true)
    setGuestSignInError('')
    try {
      const loginRes = await authAPI.login(email, password)
      const accessToken = loginRes.data.access_token as string
      const me = await authAPI.getCurrentUser(accessToken)
      onGuestAuthed?.(accessToken, me.data)
      await publishListing(pending.listingData, pending.queuedPhotosSnapshot, accessToken)
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string } }; message?: string }
      setGuestSignInError(mapGuestLoginError(ax.response?.data?.detail, ax.message || t('auth.loginFailed'), t))
    } finally {
      setLoading(false)
    }
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (imeiBlockedStolen) {
      alert(imeiError || t('listing.imeiBlockedStolen'))
      return
    }
    if (!imeiVerified && !imeiSkipped) {
      alert(t('listing.verifyFirst'))
      return
    }
    const pmid = parseInt(phoneModelId, 10)
    if (!Number.isFinite(pmid) || pmid <= 0) {
      alert(t('listing.selectModelFirst'))
      return
    }

    if (!isBasicPhone && !isSuperFast && !storage) {
      alert(t('listing.storageRequired', 'Pasirinkite atmintį (GB).'))
      return
    }
    if (!inBoxCompleteness && !isSuperFast) {
      alert(t('listing.kitRequired', 'Pasirinkite komplektaciją.'))
      return
    }

    const priceNum = parseFloat(price.replace(',', '.'))
    if (!Number.isFinite(priceNum) || priceNum <= 0) {
      setSubmitError(t('listing.priceRequired'))
      return
    }

    if (needsContact) {
      const email = sellerEmail.trim()
      const phone = sellerPhone.trim()
      if (!token && !email && !phone) {
        alert(t('listing.builder.contactMethodHint'))
        return
      }
      if (token && !canSell && !resolveContactFullName()) {
        alert(t('listing.builder.contactNeedName'))
        return
      }
    }

    setLoading(true)
    setSubmitError('')
    const yr = parseInt(releaseYear, 10)
    const submitMarks = defectMarksForSubmit(defectMarks, pendingPhotos)
    const defectDesc = formatDefectDescription(defectMarks, pendingPhotos)
    const autoTitle =
      title.trim() ||
      (selectedModel
        ? `${selectedModel.manufacturer} ${selectedModel.model_name}`
        : '')
    const listingData: Record<string, unknown> = {
      title: autoTitle,
      description: defectDesc,
      defect_marks: submitMarks.length > 0 ? submitMarks : undefined,
      price: priceNum,
      condition: conditionForListingSubmit(condition, { superFastDefault: 'good' }),
      battery_health: (() => {
        if (isBasicPhone) return undefined
        const n = parseInt(batteryHealth, 10)
        return Number.isFinite(n) ? n : undefined
      })(),
      battery_cycles: (() => {
        if (isBasicPhone) return undefined
        const n = parseInt(batteryCycles, 10)
        return Number.isFinite(n) && n >= 0 ? n : undefined
      })(),
      battery_original: isBasicPhone
        ? undefined
        : (batteryOriginalFromParts(partsAuth) ?? batteryOriginal),
      battery_tested_at: isBasicPhone || !batteryTestedAt ? undefined : batteryTestedAt,
      parts_authenticity: isBasicPhone
        ? undefined
        : compactPartsAuthenticity(mergeBatteryIntoParts(partsAuth, batteryOriginal)),
      photo_quality: suggestion?.photo_quality || undefined,
      photo_suggested_condition: suggestion?.photo_suggested_condition || undefined,
      model_match: suggestion?.model_match || undefined,
      imei: imeiSkipped ? undefined : imei,
      imei_verified: imeiVerified,
      phone_model_id: pmid,
      is_smartphone: !isBasicPhone,
      shipment_method: shipmentMethod,
      seller_city: sellerCity || userCities[0] || undefined,
      in_box_completeness: inBoxCompleteness ?? 'phone_only',
      shipping_included: shippingIncluded,
      usage_duration: usageDuration || undefined,
      has_factory_warranty: hasFactoryWarranty,
      warranty_remaining: hasFactoryWarranty ? warrantyRemaining || undefined : undefined,
    }
    if (
      !isBasicPhone &&
      Number.isFinite(yr) &&
      yr >= 2007 &&
      yr <= CURRENT_YEAR + 1
    ) {
      listingData.release_year = yr
    }
    const storageGb = parseStorageLabelToGb(storage)
    if (!isBasicPhone && storageGb != null) {
      listingData.storage_gb = storageGb
    }

    const queuedPhotosSnapshot = [...pendingPhotos]

    try {
      pendingPublishRef.current = { listingData, queuedPhotosSnapshot }
      await publishListing(listingData, queuedPhotosSnapshot, token)
    } catch (err) {
      const conflict = !token ? guestAccountConflict(err) : null
      if (conflict) {
        shouldScrollToGuestSignInRef.current = true
        setGuestSignInReason(conflict)
        setGuestSignInError('')
        setSubmitError('')
        void persistSellDraft(conflict)
        return
      }
      pendingPublishRef.current = null
      setSubmitError(formatApiError(err, t('listing.createFailed')))
    } finally {
      setLoading(false)
    }
  }

  function formatApiError(err: unknown, fallback: string): string {
    const ax = err as {
      response?: { data?: { detail?: unknown }; status?: number }
      message?: string
      code?: string
    }
    const detail = ax.response?.data?.detail
    const status = ax.response?.status

    if (status === 409 && typeof detail === 'string') {
      if (detail.toLowerCase().includes('email')) return t('listing.guestEmailExists')
      if (detail.toLowerCase().includes('phone')) return t('listing.guestPhoneExists')
      if (detail.toLowerCase().includes('imei')) return t('listing.imeiAlreadyListed')
      return detail
    }

    if (Array.isArray(detail)) {
      const msg = detail
        .map((item) => {
          if (item && typeof item === 'object' && 'msg' in item) {
            return String((item as { msg?: string }).msg)
          }
          return String(item)
        })
        .filter(Boolean)
        .join(', ')
      if (msg) return msg
    }

    if (typeof detail === 'string' && detail.trim()) return detail

    if (status === 401) return t('listing.sessionExpired')
    if (status === 403) {
      if (detail === 'site_gate_required') return t('listing.siteGateRequired')
      if (typeof detail === 'string' && /verification|patvirtinim/i.test(detail)) {
        return t('listing.sellerVerificationRequired')
      }
      if (typeof detail === 'string' && detail.trim()) return detail
      return fallback
    }
    if (status === 400) {
      if (typeof detail === 'string') {
        if (/catalog|model|phone model/i.test(detail)) return t('listing.modelUnavailable')
        return detail
      }
    }
    if (status === 500 || status === 503) {
      return typeof detail === 'string' && detail.trim()
        ? detail
        : t('listing.createServerError')
    }

    if (ax.code === 'ERR_NETWORK') return t('listing.networkError')
    if (ax.message && ax.message !== 'Network Error') return ax.message
    return fallback
  }

  function applySuggested(which: 'quick' | 'patient' | 'recommended') {
    if (!suggestion) return
    const map = {
      quick: suggestion.quick_sale_eur,
      patient: suggestion.patient_eur,
      recommended: suggestion.recommended_eur,
    }
    priceTouchedRef.current = false
    setPrice(String(map[which]))
  }

  function onPriceInputChange(value: string) {
    priceTouchedRef.current = true
    setPrice(value)
  }

  function renderSellPricingPanel() {
    if (!phoneModelId) return null
    if (isBasicPhone) {
      return (
        <div className="listing-field sell-pricing-block">
          <p className="meta-text sell-basic-phone-note">{t('listing.basicPhoneAiNote')}</p>
          <label className="sell-flow-price-field">
            {t('listing.form.price')}
            <input
              type="number"
              step="0.01"
              value={price}
              onChange={(e) => onPriceInputChange(e.target.value)}
            />
          </label>
          {PAYMENTS_ENABLED ? <SellerPayoutPreview price={price} /> : null}
        </div>
      )
    }
    return (
      <div className="listing-field sell-pricing-block">
        <span className="listing-field__label">{t('pricing.aiTitle')}</span>
        {pendingPhotos.length === 0 && <p className="meta-text">{t('listing.photosGatePricing')}</p>}
        {pricingLoading ? (
          <p className="meta-text">{t('listing.pricingCalculating', 'reDevicer AI skaičiuoja kainą…')}</p>
        ) : suggestion ? (
          <>
            {!condition ? <p className="meta-text">{t('listing.conditionSkippedPricingNote')}</p> : null}
            <p className="pricing-method">{t('pricing.methodNote')}</p>
            <div className="ai-tier-grid ai-tier-grid--3">
              <button type="button" className="ai-tier" onClick={() => applySuggested('quick')}>
                <span className="ai-tier-label">{t('pricing.tierQuick')}</span>
                <span className="ai-tier-eur">€{suggestion.quick_sale_eur.toFixed(2)}</span>
              </button>
              <button type="button" className="ai-tier ai-tier--top" onClick={() => applySuggested('recommended')}>
                <span className="ai-tier-badge">{t('pricing.topPick')}</span>
                <span className="ai-tier-label">{t('pricing.tierBalanced')}</span>
                <span className="ai-tier-eur">€{suggestion.recommended_eur.toFixed(2)}</span>
              </button>
              <button type="button" className="ai-tier" onClick={() => applySuggested('patient')}>
                <span className="ai-tier-label">{t('pricing.tierPatient')}</span>
                <span className="ai-tier-eur">€{suggestion.patient_eur.toFixed(2)}</span>
              </button>
            </div>
          </>
        ) : pricingError ? (
          <p className="meta-text">{pricingError}</p>
        ) : (
          <p className="meta-text">{t('listing.pricingCalculating', 'reDevicer AI skaičiuoja kainą…')}</p>
        )}
        <label className="sell-flow-price-field">
          {t('listing.form.price')}
          <input
            type="number"
            step="0.01"
            value={price}
            onChange={(e) => onPriceInputChange(e.target.value)}
          />
        </label>
        {PAYMENTS_ENABLED ? <SellerPayoutPreview price={price} /> : null}
        {isGreedy ? (
          <div className="pricing-greedy-warning">
            <strong>{t('pricing.greedyTitle')}</strong>
            <p>{t('pricing.greedyBody')}</p>
          </div>
        ) : null}
      </div>
    )
  }

  function renderDevicePhotoFileInputs() {
    return (
      <>
        <input
          ref={galleryInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,image/*"
          multiple
          className="sr-only"
          id="device-gallery-input"
          onChange={(e) => {
            addPendingGalleryFiles(e.target.files)
            e.target.value = ''
          }}
        />
        <input
          ref={cameraCaptureRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/*"
          capture="environment"
          className="sr-only"
          id="device-camera-input"
          onChange={(e) => {
            addPendingGalleryFiles(e.target.files)
            e.target.value = ''
          }}
        />
      </>
    )
  }

  function renderDevicePhotosPanel(panelIndex: number) {
    return (
      <SellFormPanel
        title={t('listing.sellStep3')}
        index={panelIndex}
        className="sell-form-panel--photos"
      >
        <div className="listing-form-tutorial-cta">
          <button
            type="button"
            className="listing-form-tutorial-btn"
            onClick={() => setPhotoTutorialOpen(true)}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}>
              <path d="M4 8h3l1.5-2h7L17 8h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z" />
              <circle cx="12" cy="13" r="3.2" />
            </svg>
            {t('listing.photoTutorialCta', 'Pamoka: kaip teisingai fotografuoti telefoną')}
          </button>
        </div>
        <div
          className={`device-photos-drop-zone${dragActive ? ' device-photos-drop-zone--drag' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          role="group"
          aria-label={t('listing.devicePhotosLabel')}
        >
          <div className="device-photos-drop-card">
            <p className="device-photos-drop-card__title">{t('photoAssess.uploadCtaTitle')}</p>
            {pendingPhotos.length === 0 ? (
              <p className="meta-text device-photos-optional-note">{t('listing.photosOptional')}</p>
            ) : null}
            <p className="device-photos-storage-note">{t('photoTips.storageNotice')}</p>
            <div className="device-photos-drop-card__actions">
              <button
                type="button"
                className="device-photos-btn device-photos-btn--primary"
                disabled={pendingPhotos.length >= 10}
                onClick={(e) => {
                  e.stopPropagation()
                  openGalleryPicker(e)
                }}
              >
                {t('listing.devicePhotosPick')}
              </button>
              <button
                type="button"
                className="device-photos-btn"
                disabled={pendingPhotos.length >= 10}
                onClick={(e) => {
                  e.stopPropagation()
                  cameraCaptureRef.current?.click()
                }}
              >
                {t('listing.devicePhotosCamera')}
              </button>
            </div>
          </div>
        </div>
        {photoPickError && <p className="error-text">{photoPickError}</p>}
        {pendingPhotos.length > 0 && pendingPhotos.length < 10 && (
          <div className="device-photos-actions device-photos-actions--compact">
            <button type="button" className="verify-btn imei-secondary-btn" onClick={openGalleryPicker}>
              {t('listing.devicePhotosAddMore')}
            </button>
          </div>
        )}
        {pendingPhotos.length > 0 && (
          <>
            <p className="device-photos-reorder-hint">{t('listing.photoDragHint')}</p>
            <ul className="device-photos-preview">
            {pendingPhotos.map((p, idx) => (
              <li
                key={p.id}
                className={[
                  idx === 0 ? 'device-photos-preview__item--primary' : '',
                  defectPhotoId === p.id ? 'device-photos-preview__item--defect-active' : '',
                  dragPhotoIdx === idx ? 'device-photos-preview__item--dragging' : '',
                  dragOverPhotoIdx === idx && dragPhotoIdx !== idx
                    ? 'device-photos-preview__item--drag-over'
                    : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                style={{ '--photo-i': idx } as CSSProperties}
                draggable
                onDragStart={(e) => onPhotoDragStart(idx, e)}
                onDragOver={(e) => onPhotoDragOver(idx, e)}
                onDrop={(e) => onPhotoDrop(idx, e)}
                onDragEnd={onPhotoDragEnd}
              >
                <button
                  type="button"
                  className="device-photo-select-defect"
                  onClick={() => setDefectPhotoId(p.id)}
                  aria-label={t('listing.defectMarkPhotoPicker')}
                >
                  <img src={p.url} alt="" draggable={false} />
                </button>
                {idx === 0 ? (
                  <span className="device-photo-cover-badge">{t('listing.photoPrimaryPreview')}</span>
                ) : null}
                {idx !== 0 ? (
                  <button
                    type="button"
                    className="device-photo-set-cover"
                    onClick={() => setPendingPrimary(idx)}
                  >
                    {t('listing.photoSetCover')}
                  </button>
                ) : null}
                <button
                  type="button"
                  className="device-photo-edit"
                  onClick={(e) => {
                    e.stopPropagation()
                    startEditPendingPhoto(idx)
                  }}
                >
                  {t('listing.photoCrop.edit')}
                </button>
                <button
                  type="button"
                  className="device-photo-remove"
                  onClick={(e) => {
                    e.stopPropagation()
                    removePendingPhoto(idx)
                  }}
                  aria-label={t('common.delete', 'Delete')}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden>
                    <path strokeLinecap="round" d="M6 6l12 12M18 6 6 18" />
                  </svg>
                </button>
              </li>
            ))}
          </ul>
          </>
        )}
      </SellFormPanel>
    )
  }

  function renderGuestSignInBlock() {
    return (
      <div ref={guestSignInRef} className="listing-guest-signin">
        <p className="listing-guest-signin__lead">
          {guestSignInReason === 'email'
            ? t('listing.guestEmailExists')
            : t('listing.guestPhoneExists')}
        </p>
        <p className="listing-guest-signin__hint">{t('listing.guestSignInSaved')}</p>
        {guestForgotMode ? (
          <>
            <p className="listing-guest-signin__hint">{t('auth.forgotPasswordLead')}</p>
            <label className="listing-field listing-field--modern">
              <span className="listing-field__label">{t('auth.email')}</span>
              <input
                type="email"
                value={sellerEmail}
                onChange={(e) => setSellerEmail(e.target.value)}
                autoComplete="email"
                disabled={guestForgotSent}
              />
            </label>
            {guestForgotError ? (
              <p className="listing-guest-signin__error" role="alert">
                {guestForgotError}
              </p>
            ) : null}
            {guestForgotSent ? (
              <p className="listing-guest-signin__hint">{t('auth.forgotPasswordSent')}</p>
            ) : (
              <button
                type="button"
                className="listing-guest-signin__btn"
                onClick={() => void handleGuestForgotPassword()}
                disabled={loading}
              >
                {loading ? t('listing.form.submitting') : t('auth.forgotPasswordSubmit')}
              </button>
            )}
            <button
              type="button"
              className="auth-forgot-link listing-guest-signin__back-link"
              onClick={() => {
                setGuestForgotMode(false)
                setGuestForgotSent(false)
                setGuestForgotError('')
              }}
            >
              {t('listing.guestSignInBackToLogin')}
            </button>
          </>
        ) : (
          <>
            {guestSignInReason === 'phone' || !sellerEmail.trim() ? (
              <label className="listing-field listing-field--modern">
                <span className="listing-field__label">{t('auth.email')}</span>
                <input
                  type="email"
                  value={sellerEmail}
                  onChange={(e) => setSellerEmail(e.target.value)}
                  autoComplete="email"
                />
              </label>
            ) : null}
            <label className="listing-field listing-field--modern">
              <span className="listing-field__label">{t('auth.password')}</span>
              <input
                type="password"
                value={guestSignInPassword}
                onChange={(e) => setGuestSignInPassword(e.target.value)}
                autoComplete="current-password"
              />
            </label>
            {guestSignInError ? (
              <p className="listing-guest-signin__error" role="alert">
                {guestSignInError}
              </p>
            ) : null}
            <button
              type="button"
              className="listing-guest-signin__btn"
              onClick={() => void handleGuestSignInAndContinue()}
              disabled={loading}
            >
              {loading ? t('listing.form.submitting') : t('listing.guestSignInContinue')}
            </button>
            {guestSignInReason === 'email' && sellerEmail.trim() ? (
              <button
                type="button"
                className="auth-forgot-link listing-guest-signin__back-link"
                onClick={() => {
                  setGuestForgotMode(true)
                  setGuestForgotSent(false)
                  setGuestForgotError('')
                  void persistSellDraft(guestSignInReason)
                }}
              >
                {t('auth.forgotPasswordLink')}
              </button>
            ) : null}
          </>
        )}
      </div>
    )
  }

  function renderGuestContactPanel(panelIndex: number) {
    if (!needsContact || !(imeiVerified || imeiSkipped) || imeiBlockedStolen) return null
    const showContactHint = !sellerEmail.trim() && !sellerPhone.trim()
    return (
      <fieldset
        className="listing-guest-contact sell-form-panel sell-form-panel--contact"
        style={{ '--sell-panel-i': panelIndex } as CSSProperties}
      >
        <legend className="sell-form-panel__title">{t('listing.guestContactTitle')}</legend>
        <p className="sell-form-panel__lead">{t('listing.guestContactIntro')}</p>
        <label className="listing-field listing-field--modern">
          <span className="listing-field__label">{t('sellerVerify.fullName')}</span>
          <input
            type="text"
            value={sellerFullName}
            onChange={(e) => setSellerFullName(e.target.value)}
            autoComplete="name"
          />
        </label>
        <CollapseSlot
          open={showContactHint}
          className="sell-form-contact-requirement-slot"
          ariaHiddenWhenClosed
        >
          <div className="sell-form-contact-requirement" role={showContactHint ? 'note' : undefined}>
            <span className="sell-form-contact-requirement__icon" aria-hidden="true">!</span>
            <span className="sell-form-contact-requirement__text">{t('listing.guestContactHint')}</span>
          </div>
        </CollapseSlot>
        {!token ? (
          <label className="listing-field listing-field--modern">
            <span className="listing-field__label">{t('listing.guestContactEmail')}</span>
            <input
              type="email"
              value={sellerEmail}
              onChange={(e) => setSellerEmail(e.target.value)}
              autoComplete="email"
            />
          </label>
        ) : null}
        <label className="listing-field listing-field--modern">
          <span className="listing-field__label">{t('listing.guestContactPhone')}</span>
          <input
            type="tel"
            value={sellerPhone}
            onChange={(e) => setSellerPhone(e.target.value)}
            autoComplete="tel"
          />
        </label>
        {guestSignInReason ? renderGuestSignInBlock() : null}
      </fieldset>
    )
  }

  const builderTitleSuggestions = useMemo(() => {
    if (!selectedModel) return []
    const candidates = [
      buildAutoTitle(selectedModel, storage, releaseYear),
      buildAutoTitle(selectedModel, storage, ''),
      `${selectedModel.manufacturer} ${selectedModel.model_name}`.trim(),
    ]
    const seen = new Set<string>()
    const out: string[] = []
    for (const s of candidates) {
      const v = s.trim()
      if (!v || seen.has(v) || v === title.trim()) continue
      seen.add(v)
      out.push(v)
    }
    return out.slice(0, 3)
  }, [selectedModel, storage, releaseYear, title])

  const builderContactMethodOk = Boolean(sellerEmail.trim() || sellerPhone.trim())

  const builderStepMotionClass = useMemo(() => {
    if (!steppedBuilder) return undefined
    const parts = ['builder-step']
    if (builderStepDirection === 'forward') parts.push('builder-step--enter-forward')
    else if (builderStepDirection === 'back') parts.push('builder-step--enter-back')
    return parts.join(' ')
  }, [steppedBuilder, builderStepDirection])

  const builderStepMotionKey = `${builderStep}-${builderStepDirection ?? 'idle'}`

  const builderContextValue = useMemo(
    () => ({
      enabled: steppedBuilder,
      step: builderStep,
      stepDirection: builderStepDirection,
    }),
    [steppedBuilder, builderStep, builderStepDirection],
  )

  const builderAllStepsComplete = useMemo(
    () => [1, 2, 3, 4, 5, 6, 7].every((step) => builderStepComplete[step]),
    [builderStepComplete],
  )

  const listingBuilderDraft = useMemo((): ListingBuilderDraft => {
    return {
      manufacturer: selectedModel?.manufacturer,
      modelName: selectedModel?.model_name,
      phoneModelId,
      storage,
      releaseYear,
      releaseYearUnknown,
      batteryHealth,
      condition,
      usageDuration,
      hasFactoryWarranty,
      warrantyRemaining,
      inBoxCompleteness,
      price,
      title,
      photos: pendingPhotos.map((p) => ({ id: p.id, url: p.url })),
      sellerName: sellerFullName || userPreview?.full_name,
      sellerCity,
      allStepsComplete: builderAllStepsComplete,
    }
  }, [
    selectedModel,
    phoneModelId,
    storage,
    releaseYear,
    releaseYearUnknown,
    batteryHealth,
    condition,
    usageDuration,
    hasFactoryWarranty,
    warrantyRemaining,
    inBoxCompleteness,
    price,
    title,
    pendingPhotos,
    sellerFullName,
    userPreview?.full_name,
    sellerCity,
    builderAllStepsComplete,
  ])

  useEffect(() => {
    setBuilderPreviewPhotoIndex((idx) => {
      if (pendingPhotos.length === 0) return 0
      return Math.min(idx, pendingPhotos.length - 1)
    })
  }, [pendingPhotos])

  useEffect(() => {
    if (!defectPhotoId || pendingPhotos.length === 0) return
    const idx = pendingPhotos.findIndex((p) => p.id === defectPhotoId)
    if (idx >= 0) setBuilderPreviewPhotoIndex(idx)
  }, [defectPhotoId, pendingPhotos])

  const listingPreviewViewModel = useMemo(
    () =>
      mapDraftToListingPreview(listingBuilderDraft, t, {
        activePhotoIndex: builderPreviewPhotoIndex,
      }),
    [listingBuilderDraft, t, builderPreviewPhotoIndex],
  )

  function handleBuilderPreviewThumbSelect(index: number) {
    if (index < 0 || index >= pendingPhotos.length) return
    setBuilderPreviewPhotoIndex(index)
  }

  function handleBuilderPreviewOverflowSelect() {
    if (pendingPhotos.length <= 4) return
    setBuilderPreviewPhotoIndex((current) => {
      if (current < 4) return 4
      return current >= pendingPhotos.length - 1 ? 4 : current + 1
    })
  }

  return (
    <section
      className={`listing-form-section listing-form-section--modern${
        steppedBuilder ? ' listing-form-section--builder' : ''
      }`}
    >
      <PhotoTutorialModal
        open={photoTutorialOpen}
        onClose={() => setPhotoTutorialOpen(false)}
      />
      <ListingPhotoCropModal
        open={cropSource != null}
        source={cropSource}
        fileName={cropFileName}
        onConfirm={onPhotoCropConfirm}
        onCancel={closePhotoCrop}
      />
      {(imeiVerified || imeiSkipped) && !imeiBlockedStolen ? renderDevicePhotoFileInputs() : null}
      <form
        onSubmit={handleSubmit}
        className={`listing-form listing-form--modern${isSuperFast ? ' listing-form--super-fast' : ''}${
          steppedBuilder ? ' listing-form--builder' : ''
        }${steppedBuilder && !builderWideLayout ? ' listing-form--builder-mobile' : ''}`}
      >
        <ListingBuilderContext.Provider value={builderContextValue}>
        {!steppedBuilder ? <span className="listing-form__glow" aria-hidden="true" /> : null}
        <div
          className={steppedBuilder ? 'listing-main-grid' : undefined}
          style={steppedBuilder ? undefined : { display: 'contents' }}
        >
        <div
          className={steppedBuilder ? 'listing-main-grid__main builder-panel' : undefined}
          style={steppedBuilder ? undefined : { display: 'contents' }}
        >
        {!steppedBuilder ? (
        <header className="listing-form__head listing-form__head--modern">
          {onBack ? (
            <button type="button" className="sell-path-back" onClick={onBack}>
              ← {isSuperFast ? t('listing.sellAdvancedPath') : t('listing.sellPathBack')}
            </button>
          ) : null}
          <h2>
            {isSuperFast
              ? t('listing.sellSimpleTitle')
              : showImeiSection
                ? t('listing.form.titleImei')
                : t('listing.form.title')}
          </h2>
        </header>
        ) : (
          <>
            <div className="builder-panel__chrome">
              <header className="builder-panel__head">
                {onBack && !builderWideLayout && builderStep === 1 ? (
                  <button type="button" className="sell-path-back builder-panel__back" onClick={onBack}>
                    ← {t('listing.sellPathBack')}
                  </button>
                ) : null}
                <div className="builder-panel__title-row">
                  <h2 className="builder-panel__title">{t('listing.builder.title')}</h2>
                  <button
                    type="button"
                    className="builder-panel__save-draft"
                    onClick={() => void handleBuilderSaveDraft()}
                  >
                    {t('listing.builder.saveDraft')}
                  </button>
                </div>
              </header>
              <ListingBuilderStepper
                current={builderStep}
                maxReachable={builderMaxReachable}
                onGoTo={goBuilderToStep}
                variant={builderWideLayout ? 'classic' : 'compact'}
              />
            </div>
          </>
        )}
        <div
          className={steppedBuilder ? 'builder-panel__scroll builder-step-content' : undefined}
          style={steppedBuilder ? undefined : { display: 'contents' }}
        >
        {isSuperFast ? renderGuestContactPanel(0) : null}
        <fieldset className={steppedBuilder ? 'builder-panel__fieldset' : undefined}>
        <div
          className={steppedBuilder ? builderStepMotionClass : undefined}
          key={steppedBuilder ? builderStepMotionKey : 'builder-fields'}
          style={steppedBuilder ? undefined : { display: 'contents' }}
        >
          <legend className="sr-only">{isSuperFast ? t('listing.sellSimpleTitle') : t('listing.deviceInfoLegend')}</legend>
          {showImeiSection && !steppedBuilder ? (
          <ol className="sell-flow-steps sell-flow-steps--compact" aria-label={t('listing.sellFlowAria')}>
            <li className={imeiVerified ? 'sell-flow-steps__done' : 'sell-flow-steps__active'}>
              {t('listing.sellStep1')}
            </li>
            <li
              className={
                imeiVerified && phoneModelId
                  ? 'sell-flow-steps__done'
                  : imeiVerified
                    ? 'sell-flow-steps__active'
                    : ''
              }
            >
              {t('listing.sellStep2')}
            </li>
            <li
              className={
                imeiVerified && pendingPhotos.length > 0 && suggestion
                  ? 'sell-flow-steps__done'
                  : imeiVerified && phoneModelId
                    ? 'sell-flow-steps__active'
                    : ''
              }
            >
              {t('listing.sellStep3')}
            </li>
          </ol>
          ) : null}
          {showImeiSection && (
          <BuilderStepGate step={1}>
          <label>
            {t('listing.imeiLabel')}
            <div className="imei-group imei-group--wrap">
              <input
                inputMode="numeric"
                autoComplete="off"
                value={imei}
                onChange={(e) => {
                  setImei(sanitizeImeiFieldInput(e.target.value))
                  setImeiVerified(false)
                  setImeiBlockedStolen(false)
                  setImeiEuNoBalance(false)
                  setImeiError('')
                  setImeiAutofillNote('')
                  setImeiAutofillTone('muted')
                  setImeiLookupDeviceLine('')
                  setPhoneModelId('')
                  setReleaseYear('')
                  setTitle('')
                  setImeiDeviceColor(null)
                  lastImeiHintsRef.current = null
                }}
                placeholder="35..."
              />
              <div className="imei-actions imei-actions--primary">
                <button type="button" onClick={() => verifyIMEI()} className="verify-btn imei-secondary-btn">
                  {imeiVerified ? `✓ ${t('listing.imeiVerifiedBtn')}` : t('listing.verifyImei')}
                </button>
              </div>
              <details className="imei-scan-more ui-collapse-details">
                <summary>{t('listing.imeiScanMore')}</summary>
                <div className="ui-collapse-details__panel">
                <div className="ui-collapse-details__inner imei-scan-more__inner">
                <div className="imei-actions">
                  <button type="button" className="verify-btn imei-secondary-btn" onClick={() => setImeiScannerOpen(true)}>
                    {t('listing.imeiScan.openScanner')}
                  </button>
                  <button
                    type="button"
                    className="verify-btn imei-secondary-btn"
                    disabled={imeiOcrLoading}
                    onClick={() => imeiLabelInputRef.current?.click()}
                  >
                    {imeiOcrLoading ? t('listing.imeiScan.reading') : t('listing.imeiLabelPhoto')}
                  </button>
                </div>
                </div>
                </div>
              </details>
              <input
                ref={imeiLabelInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/*"
                className="sr-only"
                onChange={(e) => {
                  handleImeiLabelPhoto(e.target.files)
                  e.target.value = ''
                }}
              />
            </div>
            {imeiError && <span className="error-text">{imeiError}</span>}
            {imeiVerified && imeiEuNoBalance && (
              <p className="photo-assess-warning" role="alert">
                {t('listing.imeiEuBalanceShort')}
              </p>
            )}
            {imeiVerified && !imeiError && !imeiEuNoBalance && (
              <span className="success-text">{t('listing.imeiVerifiedOk')}</span>
            )}
            {imeiVerified && !imeiBlockedStolen && !imeiEuNoBalance ? (
              <p className="verified-badge-selected-note">{t('listing.verifiedIncludedWithImei')}</p>
            ) : null}
            {imeiAutofillNote && (
              <p
                className={`meta-text imei-autofill-note imei-autofill-note--${imeiAutofillTone}`}
              >
                {imeiAutofillNote}
              </p>
            )}
            {imeiLookupDeviceLine && (
              <p className="meta-text imei-lookup-device-line">{imeiLookupDeviceLine}</p>
            )}
          </label>
          </BuilderStepGate>
          )}
          {showImeiSection && !steppedBuilder ? <ImeiFindGuide /> : null}

          {showImeiSection && !imeiVerified && !imeiSkipped && (
          <BuilderStepGate step={1}>
            <div className="listing-imei-skip-block">
              <button
                type="button"
                className="btn-imei-skip"
                onClick={() => { setImeiSkipped(true); setImei('') }}
              >
                {t('listing.imeiSkip')}
              </button>
            </div>
          </BuilderStepGate>
          )}

          {showImeiSection && imeiSkipped && !imeiVerified && (
          <BuilderStepGate step={1}>
            <div className="listing-imei-skip-block">
              <button
                type="button"
                className="imei-skip-undo"
                onClick={() => { setImeiSkipped(false); setImei('') }}
              >
                {t('listing.imeiSkipUndo', 'Grįžti prie IMEI')}
              </button>
            </div>
          </BuilderStepGate>
          )}

          {(imeiVerified || imeiSkipped) && !imeiBlockedStolen && (
            <>
          {isSuperFast ? renderDevicePhotosPanel(1) : null}
          <BuilderStepGate step={1}>
          <div
            className={
              steppedBuilder
                ? 'builder-step-surface builder-step-surface--flat builder-step-surface--phone'
                : undefined
            }
          >
          {steppedBuilder ? (
            <BuilderStepHead
              title={t('listing.builder.phoneSectionTitle')}
              lead={t('listing.builder.phoneSectionLead')}
            />
          ) : null}
          <div
            className={`listing-field${steppedBuilder ? ' builder-model-block' : ''}`}
            id="listing-model-anchor"
          >
            {!steppedBuilder ? (
            <span className="listing-field__label">
              {isBasicMode ? t('listing.basicPhoneSearchLabel') : t('listing.phoneModel')}
            </span>
            ) : null}
            {isBasicMode ? (
              <ListingModelPicker
                models={filteredPhoneModels}
                value={phoneModelId}
                onChange={(id) => {
                  setPhoneModelId(id)
                  const m = phoneModels.find((x) => String(x.id) === id)
                  if (m) handleBasicModelResolved(m)
                }}
                loading={modelsLoading}
                placeholder={t('listing.selectModelPlaceholder')}
                seedQuery={headerModelQuery}
                allowAnyBrand
                allowCustomLabel
                onCustomLabel={handleCustomPhoneLabel}
                showHint={false}
                variant={steppedBuilder ? 'builder-desktop' : 'default'}
                modelsLoadFailed={modelsLoadFailed}
                onRetryModels={retryPhoneModelsLoad}
              />
            ) : (
              <ListingModelPicker
                models={filteredPhoneModels}
                value={phoneModelId}
                onChange={setPhoneModelId}
                loading={modelsLoading}
                placeholder={t('listing.selectModelPlaceholder')}
                seedQuery={headerModelQuery}
                allowCustomLabel
                onCustomLabel={handleCustomPhoneLabel}
                showHint={false}
                variant={steppedBuilder ? 'builder-desktop' : 'default'}
                modelsLoadFailed={modelsLoadFailed}
                onRetryModels={retryPhoneModelsLoad}
              />
            )}
          </div>

          {!isBasicPhone && (!steppedBuilder || phoneModelId) ? (
            <div className={steppedBuilder ? 'builder-spec-row' : undefined}>
            <ListingSpecPanel
              variant="smartphone"
              storage={storage}
              onStorageChange={pickStorage}
              storageOptions={modelStorageOptions}
              releaseYearLabel={t('listing.releaseYear')}
              releaseYearWheelValue={releaseYearWheelValue}
              releaseYearUnknown={releaseYearUnknown}
              releaseYearUnknownLabel={t('listing.releaseYearUnknown', 'Nežinoma')}
              releaseYearUnknownButton={releaseYearUnknownButton}
              releaseYearPicker={releaseYearPickerInline}
              batteryLevel={batteryLevel}
              batteryTone={batteryTone}
              onBatteryChange={(n) => setBatteryHealth(String(n))}
              compactPickers={steppedBuilder}
              showBattery={false}
            />
            </div>
          ) : null}
          </div>
          </BuilderStepGate>

          {!isSuperFast ? (
            <BuilderStepGate step={2}>
            {steppedBuilder ? (
              <div className="builder-step-surface builder-step-surface--condition builder-step-surface--flat">
                <BuilderStepHead
                  title={t('listing.builder.conditionSectionTitle')}
                  lead={
                    isBasicPhone
                      ? t('listing.conditionHint')
                      : t('listing.builder.conditionSectionLead')
                  }
                />
                <div className="listing-condition-field builder-step-surface__condition">
                  <ConditionPickerGrid
                    builderDesktop
                    value={condition}
                    onChange={(v) => {
                      conditionTouchedRef.current = true
                      setCondition(v)
                    }}
                  />
                </div>
                {!isBasicPhone ? (
                  <section className="builder-condition-detail" aria-labelledby="builder-condition-detail-title">
                    <header className="builder-condition-detail__head">
                      <h4 id="builder-condition-detail-title" className="builder-condition-detail__title">
                        {t('listing.builder.conditionDetailTitle')}
                      </h4>
                      <p className="builder-condition-detail__lead">{t('listing.builder.conditionDetailLead')}</p>
                    </header>
                    <PartsAuthenticityFields
                      value={partsAuth}
                      onChange={setPartsAuth}
                      manufacturer={selectedModel?.manufacturer}
                      modelName={selectedModel?.model_name}
                      builderFlat
                      omitPartIds={['battery']}
                    />
                  </section>
                ) : null}
              </div>
            ) : (
              <div className={`builder-step__condition-grid builder-step__condition-grid--stack`}>
                <div className="listing-field listing-condition-field listing-condition-field--option-caps">
                  <span className="listing-field__label">{t('listing.conditionLabel')}</span>
                  <p className="listing-condition-lead">{t('listing.conditionHint')}</p>
                  <ConditionPickerGrid
                    value={condition}
                    onChange={(v) => {
                      conditionTouchedRef.current = true
                      setCondition(v)
                    }}
                  />
                </div>
                {!isBasicPhone ? (
                  <PartsAuthenticityFields
                    value={partsAuth}
                    onChange={setPartsAuth}
                    manufacturer={selectedModel?.manufacturer}
                    modelName={selectedModel?.model_name}
                    defaultCollapsed={isSuperFast}
                    omitPartIds={['battery']}
                  />
                ) : null}
              </div>
            )}
            </BuilderStepGate>
          ) : null}

          {isSuperFast && phoneModelId ? (
            <div className="sell-simple-block">
              <label className="sell-flow-price-field sell-flow-price-field--hero">
                {t('listing.sellSimplePrice')}
                <input
                  type="number"
                  step="0.01"
                  inputMode="decimal"
                  value={price}
                  onChange={(e) => onPriceInputChange(e.target.value)}
                  placeholder="350"
                />
              </label>
              {PAYMENTS_ENABLED ? <SellerPayoutPreview price={price} /> : null}
            </div>
          ) : null}

          {isSuperFast && phoneModelId ? (
            <>
              <button
                type="button"
                className="sell-more-toggle"
                onClick={() => setShowMoreSettings((v) => !v)}
                aria-expanded={showMoreSettings}
              >
                {showMoreSettings ? `▲ ${t('listing.sellMoreSettings')}` : `▼ ${t('listing.sellMoreSettings')}`}
              </button>
              <CollapseSlot open={showMoreSettings} classNameWhenOpen="sell-more-settings-slot--open">
                <div className="listing-field listing-condition-field listing-condition-field--option-caps">
                  <span className="listing-field__label">{t('listing.conditionLabel')}</span>
                  <ConditionPickerGrid
                    value={condition}
                    onChange={setCondition}
                  />
                </div>
                <WarrantyFields
                  hasWarranty={hasFactoryWarranty}
                  warrantyRemaining={warrantyRemaining}
                  onHasWarrantyChange={setHasFactoryWarranty}
                  onWarrantyRemainingChange={setWarrantyRemaining}
                />
              </CollapseSlot>
            </>
          ) : null}

          {!isSuperFast ? (
          <BuilderStepGate step={3}>
          {steppedBuilder ? (
            <div className="builder-step-surface builder-step-surface--usage builder-step-surface--flat">
              <BuilderStepHead title={t('listing.builder.historySectionTitle')} />
              <div className="builder-history-main-row">
                <div className="builder-history-main-col">
                  <section
                    className="builder-usage-duration"
                    aria-label={t('listing.builder.usageDurationTitle')}
                  >
                    <UsageDurationPickerGrid
                      builderSpecCell
                      value={usageDuration}
                      onChange={setUsageDuration}
                    />
                  </section>
                  <WarrantyFields
                    embedded
                    hasWarranty={hasFactoryWarranty}
                    warrantyRemaining={warrantyRemaining}
                    onHasWarrantyChange={setHasFactoryWarranty}
                    onWarrantyRemainingChange={setWarrantyRemaining}
                  />
                </div>
                {!isBasicPhone ? (
                  <BatteryTransparencyBuilderPanel
                    batteryHealth={batteryHealth}
                    onBatteryHealthChange={setBatteryHealth}
                    batteryLevel={batteryLevel}
                    batteryTone={batteryTone}
                    batteryCycles={batteryCycles}
                    onBatteryCyclesChange={setBatteryCycles}
                    batteryTestedAt={batteryTestedAt}
                    onBatteryTestedAtChange={setBatteryTestedAt}
                    partsAuth={partsAuth}
                    onPartsAuthChange={setPartsAuth}
                    batteryOriginal={batteryOriginal}
                    onBatteryOriginalChange={setBatteryOriginal}
                  />
                ) : null}
              </div>
            </div>
          ) : (
            <>
              <div className="builder-step__usage-row builder-step__condition-grid--stack">
                {!isBasicPhone ? (
                <div className="listing-field listing-condition-field listing-condition-field--option-caps">
                  <span className="listing-field__label">{t('listing.conditionLabel')}</span>
                  <p className="listing-condition-lead">{t('listing.conditionHint')}</p>
                  <ConditionPickerGrid
                    value={condition}
                    onChange={(v) => {
                      conditionTouchedRef.current = true
                      setCondition(v)
                    }}
                  />
                  {!condition && !isSuperFast ? (
                    <p className="meta-text sell-form-panel__note">{t('listing.conditionOptionalNote')}</p>
                  ) : null}
                </div>
                ) : null}
                <div className="listing-field listing-condition-field">
                  <span className="listing-field__label">{t('listing.usageLegend')}</span>
                  <p className="listing-condition-lead">{t('listing.usageIntro')}</p>
                  <UsageDurationPickerGrid value={usageDuration} onChange={setUsageDuration} />
                </div>
              </div>
              {!isBasicPhone ? (
                <BatteryTransparencyBuilderPanel
                  batteryHealth={batteryHealth}
                  onBatteryHealthChange={setBatteryHealth}
                  batteryLevel={batteryLevel}
                  batteryTone={batteryTone}
                  batteryCycles={batteryCycles}
                  onBatteryCyclesChange={setBatteryCycles}
                  batteryTestedAt={batteryTestedAt}
                  onBatteryTestedAtChange={setBatteryTestedAt}
                  partsAuth={partsAuth}
                  onPartsAuthChange={setPartsAuth}
                  batteryOriginal={batteryOriginal}
                  onBatteryOriginalChange={setBatteryOriginal}
                />
              ) : null}
              <WarrantyFields
                hasWarranty={hasFactoryWarranty}
                warrantyRemaining={warrantyRemaining}
                onHasWarrantyChange={setHasFactoryWarranty}
                onWarrantyRemainingChange={setWarrantyRemaining}
                panelIndex={4}
              />
            </>
          )}
          </BuilderStepGate>
          ) : null}

          {!isSuperFast ? (
          <BuilderStepGate step={4}>
          {steppedBuilder ? (
            <div className="builder-step-surface builder-step-surface--kit builder-step-surface--flat">
              <BuilderStepHead
                title={t('listing.builder.kitSectionTitle')}
                lead={t('listing.builder.kitSectionLead')}
              />
              <InBoxPickerGrid builderDesktop value={inBoxCompleteness} onChange={setInBoxCompleteness} />
            </div>
          ) : (
            <div className="listing-field listing-condition-field listing-condition-field--option-caps">
              <span className="listing-field__label">{t('listing.kitLegend')}</span>
              <p className="listing-condition-lead">{t('listing.kitIntro')}</p>
              <InBoxPickerGrid value={inBoxCompleteness} onChange={setInBoxCompleteness} />
              {!inBoxCompleteness ? (
                <p className="sell-form-panel__note">{t('listing.kitRequired', 'Pasirinkite komplektaciją.')}</p>
              ) : null}
            </div>
          )}
          </BuilderStepGate>
          ) : null}

          {!isSuperFast ? (
          <BuilderStepGate step={5}>
            {steppedBuilder ? (
              <BuilderPhotosStep
                photos={pendingPhotos.map((p) => ({ id: p.id, url: p.url, file: p.file }))}
                activePhotoId={defectPhotoId}
                onActivePhotoIdChange={setDefectPhotoId}
                onRefreshPhotoUrl={refreshPendingPhotoUrl}
                marks={defectMarks}
                onMarksChange={setDefectMarks}
                selectedDefectId={selectedDefectId}
                onSelectedDefectIdChange={setSelectedDefectId}
                onOpenTutorial={() => setPhotoTutorialOpen(true)}
                onAddPhotos={() => openGalleryPicker()}
                onAddCamera={() => {
                  if (pendingPhotos.length >= 10) return
                  cameraCaptureRef.current?.click()
                }}
                photoPickError={photoPickError}
                dragActive={dragActive}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                canAddMore={pendingPhotos.length < 10}
                onRemovePhoto={removePendingPhoto}
                onEditPhoto={startEditPendingPhoto}
                onSetCover={setPendingPrimary}
                conditionUndocumentedCount={photosConditionHint.count}
                conditionUndocumentedMessage={photosConditionHint.message}
              />
            ) : (
              renderDevicePhotosPanel(6)
            )}
          </BuilderStepGate>
          ) : null}

          {!isSuperFast && pendingPhotos.length > 0 && !steppedBuilder ? (
          <BuilderStepGate step={5}>
            <SellFormPanel
              title={t('listing.defectMarkTitle')}
              lead={t('listing.defectMarkHint')}
              index={7}
              className="sell-form-panel--defects"
            >
              <DefectMarksMultiPhoto
                photos={pendingPhotos.map((p, idx) => ({
                  id: p.id,
                  url: p.url,
                  isCover: idx === 0,
                }))}
                marks={defectMarks}
                onChange={setDefectMarks}
                activePhotoId={defectPhotoId}
                onActivePhotoChange={setDefectPhotoId}
                embedded
              />
            </SellFormPanel>
          </BuilderStepGate>
          ) : null}

          {!isSuperFast && phoneModelId ? (
          <BuilderStepGate step={6}>
            {steppedBuilder ? (
              <BuilderPriceStep
                isBasicPhone={isBasicPhone}
                price={price}
                onPriceChange={onPriceInputChange}
                suggestion={suggestion}
                pricingLoading={pricingLoading}
                pricingError={pricingError}
                hasCoverPhoto={pendingPhotos.length > 0}
                condition={condition}
                onApplyTier={applySuggested}
                isGreedy={Boolean(isGreedy)}
                paymentsEnabled={PAYMENTS_ENABLED}
              />
            ) : (
              renderSellPricingPanel()
            )}
          </BuilderStepGate>
          ) : null}

          {!isSuperFast ? (
          <BuilderStepGate step={7}>
            {steppedBuilder ? (
              <BuilderContactStep
                title={title}
                onTitleChange={(value) => {
                  titleTouchedRef.current = true
                  setTitle(value)
                }}
                titleSuggestions={builderTitleSuggestions}
                onApplyTitleSuggestion={(value) => {
                  titleTouchedRef.current = true
                  setTitle(value)
                }}
                sellerCity={sellerCity}
                onSellerCityChange={setSellerCity}
                userCities={userCities}
                catalogCityOptions={catalogCityOptions}
                onOpenAccountSettings={onOpenAccountSettings}
                needsContact={needsContact}
                isLoggedIn={Boolean(token)}
                sellerFullName={sellerFullName}
                onSellerFullNameChange={setSellerFullName}
                sellerEmail={sellerEmail}
                onSellerEmailChange={setSellerEmail}
                sellerPhone={sellerPhone}
                onSellerPhoneChange={setSellerPhone}
                showFullNameField={Boolean(token) && needsContact}
                phoneDialCode={builderPhoneDial}
                phoneDialOptions={BUILDER_PHONE_DIAL_OPTIONS}
                onPhoneDialCodeChange={setBuilderPhoneDial}
                guestSignInPanel={guestSignInReason ? renderGuestSignInBlock() : null}
              />
            ) : (
              <>
                <label>
                  {t('listing.form.titleField')}
                  <input
                    value={title}
                    onChange={(e) => {
                      titleTouchedRef.current = true
                      setTitle(e.target.value)
                    }}
                  />
                </label>
                <SellFormPanel title={t('listing.sellerCityLegend')} lead={t('listing.sellerCityIntro')} index={8}>
                  {(() => {
                    const pickable =
                      token && userCities.length > 0 ? userCities : catalogCityOptions
                    if (pickable.length > 0) {
                      return (
                        <label className="listing-field listing-field--modern">
                          <span className="listing-field__label">{t('listing.sellerCityLabel')}</span>
                          <select
                            value={sellerCity}
                            onChange={(e) => setSellerCity(e.target.value)}
                          >
                            <option value="">{t('listing.builder.contactLocationAddCta')}</option>
                            {pickable.map((city) => (
                              <option key={city} value={city}>
                                {city}
                              </option>
                            ))}
                          </select>
                        </label>
                      )
                    }
                    return (
                      <label className="listing-field listing-field--modern">
                        <span className="listing-field__label">{t('listing.sellerCityLabel')}</span>
                        <input
                          type="text"
                          value={sellerCity}
                          onChange={(e) => setSellerCity(e.target.value)}
                          placeholder={t('listing.builder.contactLocationAddCta')}
                          maxLength={80}
                          autoComplete="address-level2"
                        />
                      </label>
                    )
                  })()}
                </SellFormPanel>
                {renderGuestContactPanel(9)}
              </>
            )}
          </BuilderStepGate>
          ) : null}

            </>
          )}
        </div>
        </fieldset>
        </div>

        {steppedBuilder && (imeiVerified || imeiSkipped) && !imeiBlockedStolen ? (
          <div className="builder-panel__footer">
            <div className="builder-panel__footer-messages" aria-live="polite">
              {builderStep === 7 ? (
                <>
                  {!canSubmit &&
                  submitBlockers[0] &&
                  !(builderStep === 7 && needsContact && !token && !builderContactMethodOk) ? (
                    <p className="listing-submit-hint builder-panel__submit-hint" role="status">
                      {submitBlockers[0]}
                    </p>
                  ) : null}
                  {submitError ? (
                    <p className="listing-submit-error builder-panel__submit-hint" role="alert">
                      {submitError}
                    </p>
                  ) : null}
                </>
              ) : null}
            </div>
            <div
              className={`builder-panel__nav builder-panel__actions${
                !builderWideLayout && builderStep <= 1 ? ' builder-panel__nav--forward-only' : ''
              }`}
            >
              {builderWideLayout || builderStep > 1 ? (
                <button
                  type="button"
                  className="builder-panel__nav-back"
                  disabled={builderStep <= 1}
                  onClick={goBuilderBack}
                >
                  {t('listing.builder.back')}
                </button>
              ) : null}
              <div className="builder-panel__nav-forward">
                {builderStep < 7 ? (
                  <button
                    type="button"
                    className="builder-panel__nav-next"
                    disabled={!builderCanContinue}
                    onClick={goBuilderNext}
                    aria-disabled={!builderCanContinue}
                  >
                    {t('listing.builder.continue')}
                  </button>
                ) : (
                  <button
                    type="submit"
                    className="builder-panel__submit-listing builder-panel__nav-next"
                    disabled={!canSubmit || loading || modelsLoading}
                  >
                    {loading ? t('listing.form.submitting', 'Kuriama…') : t('listing.builder.submitListing')}
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : null}
        </div>

        {builderWideLayout ? (
          <div className="listing-preview-wrapper">
            <ListingPreview
              viewModel={listingPreviewViewModel}
              interactiveThumbs={pendingPhotos.length > 0}
              onThumbSelect={handleBuilderPreviewThumbSelect}
              onOverflowThumbSelect={handleBuilderPreviewOverflowSelect}
            />
          </div>
        ) : null}
        </div>

        {!steppedBuilder && (imeiVerified || imeiSkipped) && !imeiBlockedStolen && (
        <>
        <button
          type="submit"
          disabled={!canSubmit || loading || modelsLoading}
        >
          {loading ? t('listing.form.submitting', 'Kuriama…') : isSuperFast ? t('listing.sellSubmitNow') : t('listing.form.submit')}
        </button>
        {!canSubmit && submitBlockers[0] ? (
          <p className="listing-submit-hint" role="status">
            {submitBlockers[0]}
          </p>
        ) : null}
        {submitError ? (
          <p className="listing-submit-error" role="alert">
            {submitError}
          </p>
        ) : null}
        </>
        )}
        </ListingBuilderContext.Provider>
      </form>

      <ImeiScannerModal
        isOpen={imeiScannerOpen}
        authToken={token ?? undefined}
        onClose={() => setImeiScannerOpen(false)}
        onImeiFound={(digits) => {
          setImei(digits)
          setImeiVerified(false)
          setImeiBlockedStolen(false)
          setImeiAutofillNote('')
          setImeiAutofillTone('muted')
          verifyIMEI(digits)
        }}
      />


    </section>
  )
}
